import { useState, useEffect, useMemo, useRef, useCallback } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { shipmentService } from "../../services/shipmentService"
import shipmentEditRequestService from "../../services/shipmentEditRequestService.js"
import { buildAgentProposedFromEditForm } from "../../utils/shipmentEditRequest.js"
import { paymentService } from "../../services/ShipmentPaymentService.js"
import shipmentClientService from "../../services/ShipmentClientService.js"
import companySettingsService from "../../services/companySettingsService.js"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader.js"
import Alert from "../common/Alert"
import { MapPin, ArrowLeft, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from "lucide-react"
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js'
import Button from '../common/Button.js'
import { roundPaise } from '../../utils/formatCurrency.js';
import DoorVehicleChoice from './DoorVehicleChoice.js';
import { getLocalDateString } from '../../utils/localDate.js';
import {
  formatHubBranchLabel,
  filterShipmentLocations,
  filterBranchLocations,
} from '../../utils/destinationType.js'
import { compareLocationsByNumber } from '../../utils/locationDropdownLabel.js'
import {
  getSuggestionListClass,
  getSuggestionItemClass,
  handleSuggestionDropdownKeyDown,
  scrollHighlightedSuggestionIntoView,
} from '../../utils/suggestionDropdown.js'
import ShipmentMediaUploader from './ShipmentMediaUploader.js';
import GstVerificationField from './GstVerificationField.js';
import { deriveBookingGst } from './rcmGst.js';
import {
  PackageDimensionSummary,
  AddPackageDimensionModal,
  EditPackageDimensionModal,
  ViewPackageDimensionModal,
} from './package-dimension/index.js';
import {
  emptyBusArticle,
  sumChargeableWeight,
  sumParcelQty,
  sumBusArticleWeight,
  calcFreightFromRate,
  calcBusFreight,
  parcelsForApi,
  parcelsFromApi,
  busArticlesFromApi,
  resolveCargoDefaults,
  calcInsuranceFromSettings,
  syncFormFromBusArticles,
  toNum,
  normalizeChargeMode,
  isShipmentChargeSplitMode,
  allocateShipmentChargeSplit,
  chargeDefaultsWithoutLabourSplit,
  EXTRA_SHIPMENT_CHARGE_KEYS,
  EXTRA_CHARGE_FORM_FIELDS,
  PER_PARCEL_CHARGE_KEYS,
  resolvePerParcelChargeAmount,
  perParcelChargeTotal,
  perParcelUnitRate,
  shipmentParcelsAreGstExempt,
} from '../../utils/cargoParcelUtils.js';
import { validateBusArticles } from '../../utils/cargoParcelUtils.js';
import { DEFAULT_LABOUR_CHARGE_POLICY, labourCapturedAt, normalizeLabourChargePolicy } from '../../utils/labourChargePolicy.js';
import BusArticlePackagesForm from './BusArticlePackagesForm.js';
import { hasInternalHubDispatchControls } from '../../utils/networkMode.js';
import { isExternalNetworkCompany, mergeCanAccess } from '../../utils/companyFeatures.js';

/** Optional Rate/KG from a location hub (receiver). Empty/missing → no override. */
const rateFromHub = (hub) => {
  if (!hub || hub.rate_per_kg === null || hub.rate_per_kg === undefined || hub.rate_per_kg === '') {
    return null
  }
  const n = Number(hub.rate_per_kg)
  if (!Number.isFinite(n) || n < 0) return null
  return String(n)
}

const MOBILE_STEPS = [
  { id: "location", label: "Location" },
  { id: "consignor", label: "Consignor" },
  { id: "consignee", label: "Consignee" },
  { id: "details", label: "Shipment" },
  { id: "payment", label: "Payment" },
]

const SHIPMENT_PAYMENT_MODES = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
]

const paymentOptionsForHub = (hub) => [
  hub?.allow_to_pay !== false && { value: "to_pay", label: "To Pay" },
  hub?.allow_paid !== false && { value: "paid", label: "Paid" },
  hub && hub.allow_on_account !== false && { value: "credit", label: "Credit" },
].filter(Boolean)

const normalizeShipmentPaymentMode = (mode) => {
  if (!mode) return ""
  const normalized = String(mode).toLowerCase().trim().replace(/\s+/g, "_")
  const aliases = {
    online_transfer: "online",
    bank: "bank_transfer",
    card_payment: "online",
    card: "online",
  }
  return aliases[normalized] || normalized
}

const resolveShipmentPaymentMode = (shipment, paymentHistory = []) => {
  const fromShipment = normalizeShipmentPaymentMode(shipment?.payment_mode)
  if (fromShipment) return fromShipment

  const history = Array.isArray(paymentHistory) ? paymentHistory : []
  const bookingPayment = history.find(
    (payment) => payment?.payment_mode && String(payment.type || "").toLowerCase() === "booking",
  )
  const paymentWithMode = bookingPayment || history.find((payment) => payment?.payment_mode)
  return normalizeShipmentPaymentMode(paymentWithMode?.payment_mode)
}

const EWAY_BILL_MANDATORY_VALUE = 100000
const EWAY_BILL_MANDATORY_LABEL = "₹1,00,000"

const EditShipment = () => {
  const { shipmentId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const companyAccess = mergeCanAccess(user?.company_features || user?.company_can_access || {})
  const ewaybillFeatureOn = Boolean(companyAccess.ewaybill)
  const [gstVerificationOn, setGstVerificationOn] = useState(
    Boolean(companyAccess.gst_verification)
  )
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [consignerLocations, setConsignerLocations] = useState([])
  const [consigneeLocations, setConsigneeLocations] = useState([])
  const [consigneeLocationSearch, setConsigneeLocationSearch] = useState("")
  const [showConsigneeDropdown, setShowConsigneeDropdown] = useState(false)
  const [bookingAgents, setBookingAgents] = useState([])
  const [bookingAgentUserId, setBookingAgentUserId] = useState("")
  const [bookingAgentsLoading, setBookingAgentsLoading] = useState(false)
  const [showBookingAgentDropdown, setShowBookingAgentDropdown] = useState(false)
  const [bookingAgentSearch, setBookingAgentSearch] = useState("")
  const [destinationAgents, setDestinationAgents] = useState([])
  const [destinationAgentUserId, setDestinationAgentUserId] = useState("")
  const [destinationAgentsLoading, setDestinationAgentsLoading] = useState(false)
  const [showDestinationAgentDropdown, setShowDestinationAgentDropdown] = useState(false)
  const [destinationAgentSearch, setDestinationAgentSearch] = useState("")
  const [shipmentOwnerUserId, setShipmentOwnerUserId] = useState("")
  const [loadedSenderHubId, setLoadedSenderHubId] = useState("")
  const [loadedReceiverHubId, setLoadedReceiverHubId] = useState("")
  const [loadedReceiverAgentUserId, setLoadedReceiverAgentUserId] = useState("")
  const [destinationAgentNeedsSelection, setDestinationAgentNeedsSelection] = useState(false)
  const [showConsignerDropdown, setShowConsignerDropdown] = useState(false)
  const [consignerLocationSearch, setConsignerLocationSearch] = useState("")
  const [consignerHighlightIndex, setConsignerHighlightIndex] = useState(-1)
  const bookingAgentDropdownRef = useRef(null)
  const destinationAgentDropdownRef = useRef(null)
  const consignerRef = useRef(null)
  const consigneeRef = useRef(null)
  const paidPaymentFieldsRef = useRef(null)
  const parcelImagesRef = useRef(null)
  const prevModeOfPaymentRef = useRef("to_pay")
  const insuranceFreightSyncedRef = useRef(null)

  const [showAdditionalCharges, setShowAdditionalCharges] = useState(false)
  const [showParcelImages, setShowParcelImages] = useState(false)
  const [showGSTDetails, setShowGSTDetails] = useState(false)
  const [showAutoMlSection, setShowAutoMlSection] = useState(false)
  const [consigneeHighlightIndex, setConsigneeHighlightIndex] = useState(-1)

  const [mobileStep, setMobileStep] = useState(0)
  const mobileStepRef = useRef(0)
  const [isMobileLayout, setIsMobileLayout] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 1024px)").matches
  )

  useEffect(() => {
    mobileStepRef.current = mobileStep
  }, [mobileStep])

  const [useShipmentClient, setUseShipmentClient] = useState(false)
  const [shipmentClients, setShipmentClients] = useState([])
  const [clientSearch, setClientSearch] = useState("")
  const [showClientDropdown, setShowClientDropdown] = useState(false)
  const [selectedClient, setSelectedClient] = useState(null)
  const [clientHighlightIndex, setClientHighlightIndex] = useState(-1)

  const [consignorSuggestions, setConsignorSuggestions] = useState([])
  const [showConsignorSuggestions, setShowConsignorSuggestions] = useState(false)
  const [consignorSuggestionsLoading, setConsignorSuggestionsLoading] = useState(false)
  const [consignorSuggestionHighlightIndex, setConsignorSuggestionHighlightIndex] = useState(-1)
  const consignorDebounceRef = useRef(null)

  const [consigneeSuggestions, setConsigneeSuggestions] = useState([])
  const [showConsigneeSuggestions, setShowConsigneeSuggestions] = useState(false)
  const [consigneeSuggestionsLoading, setConsigneeSuggestionsLoading] = useState(false)
  const [consigneeSuggestionHighlightIndex, setConsigneeSuggestionHighlightIndex] = useState(-1)
  const consigneeDebounceRef = useRef(null)
  const skipCreditRematchRef = useRef("")

  const [shipmentIdType, setShipmentIdType] = useState('auto')
  const [consignmentNo, setConsignmentNo] = useState(null)

  const [showEwayBillModal, setShowEwayBillModal] = useState(false)
  const [ewayBills, setEwayBills] = useState([""])
  const [submittedEwayBills, setSubmittedEwayBills] = useState([])
  const [existingEwayBills, setExistingEwayBills] = useState([])
  const [loadingEwayBills, setLoadingEwayBills] = useState(false)
  const [showUpdateVehicleModal, setShowUpdateVehicleModal] = useState(false)
  const [ewayActionLoading, setEwayActionLoading] = useState(false)
  const [selectedEwayBill, setSelectedEwayBill] = useState(null)
  const [updateVehicleForm, setUpdateVehicleForm] = useState({
    vehicleNo: '',
    reasonRem: 'Vehicle Update',
  })

  // Store original charges to track if Agent reduced them
  const [agentAllowedFields, setAgentAllowedFields] = useState([])
  const [originalCharges, setOriginalCharges] = useState({
    handling_charges: "",
    delivery_charges: "",
    door_pickup_charges: "",
    late_charges: "",
    docket_fee: "10",
    other_charges: "",
    discount: "",
  })

  const [formData, setFormData] = useState({
    consigner_name: "",
    consigner_address: "",
    consigner_location: "",
    consigner_contact: "",
    consigner_email: "",
    consigner_gst: "",
    consignee_name: "",
    consignee_address: "",
    consignee_location: "",
    consignee_contact: "",
    consignee_email: "",
    consignee_gst: "",
    invoice_number: "",
    invoice_value: "",
    booking_date: getLocalDateString(),
    no_of_articles: 1,
    article_type: "",
    article_amount: "",
    weight: "",
    shipment_value: "",
    freight_charges: "",
    handling_charges: "",
    delivery_charges: "",
    door_pickup_charges: "",
    door_pickup_vehicle: "own",
    door_delivery_vehicle: "own",
    late_charges: "",
    docket_fee: "10",
    other_charges: "",
    insurance_charges: "",
    discount: "",
    rate_per_kg: "",
    loading_charges: "",
    unloading_charges: "",
    toll_charges: "",
    fuel_surcharge: "",
    packing_charges: "",
    delivery_type: "hub_delivery",
    mode_of_payment: "to_pay",
    payment_mode: "",
    pickup_type: "partial_pickup",
    parcel_detail: "",
    parcel_image_1: null,
    parcel_image_2: null,
    paid_amount: "",
    sgst_percentage: "",
    cgst_percentage: "",
    igst_percentage: "",
    status: "Booked",
  })

  // RCM (Reverse Charge Mechanism) company setting
  const [companyRcm, setCompanyRcm] = useState({ enabled: false, igst: 0, cgst: 0, sgst: 0 })
  const [companyGstInfo, setCompanyGstInfo] = useState({ gst_number: "", state: "" })
  const [senderGstLookup, setSenderGstLookup] = useState({ status: 'idle', data: null, error: '' })
  const [receiverGstLookup, setReceiverGstLookup] = useState({ status: 'idle', data: null, error: '' })
  const onSenderGstLookup = useCallback((next) => {
    setSenderGstLookup((prev) => (
      prev.status === next.status && prev.data === next.data && prev.error === next.error
        ? prev
        : next
    ))
  }, [])
  const onReceiverGstLookup = useCallback((next) => {
    setReceiverGstLookup((prev) => (
      prev.status === next.status && prev.data === next.data && prev.error === next.error
        ? prev
        : next
    ))
  }, [])
  const [isCargoMode, setIsCargoMode] = useState(false)
  const [busFreightByWeight, setBusFreightByWeight] = useState(false)
  const [chargeDefaults, setChargeDefaults] = useState(resolveCargoDefaults())
  const [bulkLoad, setBulkLoad] = useState(false)
  const [labourPolicy, setLabourPolicy] = useState(DEFAULT_LABOUR_CHARGE_POLICY)
  const [parcels, setParcels] = useState([emptyBusArticle(1)])
  const parcelCountForCharges = useMemo(() => sumParcelQty(parcels), [parcels])
  const gstExempt = useMemo(() => shipmentParcelsAreGstExempt(parcels), [parcels])
  const loadingAtBooking = labourCapturedAt(labourPolicy, 'loading', 'booking')
  const unloadingAtBooking = labourCapturedAt(labourPolicy, 'unloading', 'booking')
  const bookingHub = useMemo(
    () => (consignerLocations || []).find((hub) =>
      String(hub.id) === String(formData.sender_location_hub_id || user?.location_hub_id || ''),
    ) || null,
    [consignerLocations, formData.sender_location_hub_id, user?.location_hub_id],
  )
  const userFromSenderHub = !!(
    bookingHub
    && user?.location_hub_id
    && String(user.location_hub_id) === String(bookingHub.id)
  )
  const adminBooksAgentBranch = user?.role === 'Admin' || user?.role === 'System Admin'
  const showBulkLoadOption = !!(
    bookingHub?.bulk_load && (userFromSenderHub || adminBooksAgentBranch)
  )
  const bulkLoadActive = showBulkLoadOption && bulkLoad
  const splitChargeDefaults = useMemo(
    () => chargeDefaultsWithoutLabourSplit(chargeDefaults, bulkLoadActive),
    [chargeDefaults, bulkLoadActive],
  )
  const chargeSplitMode = isShipmentChargeSplitMode(splitChargeDefaults, labourPolicy)
  const articleFreightBase = useMemo(() => {
    if (isCargoMode) {
      return calcFreightFromRate(toNum(formData.rate_per_kg), sumChargeableWeight(parcels))
    }
    return calcBusFreight(parcels, formData.rate_per_kg, busFreightByWeight)
  }, [isCargoMode, parcels, formData.rate_per_kg, busFreightByWeight])
  const chargeSplit = useMemo(() => {
    const formAmounts = {}
    EXTRA_SHIPMENT_CHARGE_KEYS.forEach((key) => {
      if (key === 'loading' && !loadingAtBooking) return
      if (key === 'unloading' && !unloadingAtBooking) return
      formAmounts[key] = formData[EXTRA_CHARGE_FORM_FIELDS[key]]
    })
    return allocateShipmentChargeSplit(splitChargeDefaults, articleFreightBase, formAmounts, labourPolicy)
  }, [
    splitChargeDefaults,
    articleFreightBase,
    formData,
    loadingAtBooking,
    unloadingAtBooking,
    labourPolicy,
  ])

  const resolveChargeAmount = (chargeKey, fieldName) =>
    resolvePerParcelChargeAmount({
      chargeKey,
      unitRate: formData[fieldName],
      parcelCount: parcelCountForCharges,
      chargeDefaults,
    })

  const [showAddPackageModal, setShowAddPackageModal] = useState(false)
  const [addPackageAppendMode, setAddPackageAppendMode] = useState(false)
  const [showEditPackageModal, setShowEditPackageModal] = useState(false)
  const [showViewPackageModal, setShowViewPackageModal] = useState(false)

  const [shipmentMediaFiles, setShipmentMediaFiles] = useState([])
  const [existingMedia, setExistingMedia] = useState([])
  const [removedMediaIds, setRemovedMediaIds] = useState([])

  useEffect(() => {
    const onMediaError = (e) => {
      if (e?.detail) Alert.error(String(e.detail))
    }
    window.addEventListener('shipment-media-error', onMediaError)
    return () => window.removeEventListener('shipment-media-error', onMediaError)
  }, [])

  const { errors, validate, validateField } = useValidation()

  const validationRules = {
    consigner_name:     (v) => validators.required(v, 'Consignor name'),
    consigner_contact:  (v) => validators.required(v, 'Consignor contact') || validators.contact(v),
    consigner_location: (v) => validators.required(v, 'From location'),
    consigner_email:    (v) => v ? validators.email(v) : '',
    consigner_gst:      (v) => {
      const text = String(v || "").trim()
      if ((Number.parseInt(formData.invoice_value, 10) || 0) > EWAY_BILL_MANDATORY_VALUE && !text) {
        return `Consignor GST number is required when invoice value is more than ${EWAY_BILL_MANDATORY_LABEL}`
      }
      return text ? validators.gst(text) : ""
    },
    consignee_name:     (v) => validators.required(v, 'Consignee name'),
    consignee_contact:  (v) => validators.required(v, 'Consignee contact') || validators.contact(v),
    consignee_location: (v) => validators.required(v, 'Destination'),
    consignee_email:    (v) => v ? validators.email(v) : '',
    consignee_gst:      (v) => {
      const text = String(v || "").trim()
      if ((Number.parseInt(formData.invoice_value, 10) || 0) > EWAY_BILL_MANDATORY_VALUE && !text) {
        return `Consignee GST number is required when invoice value is more than ${EWAY_BILL_MANDATORY_LABEL}`
      }
      return text ? validators.gst(text) : ""
    },
    article_type:       () => '',
    no_of_articles:     () => '',
    article_amount:     () => '',
  }

  const handleBusArticlesChange = (nextArticles) => {
    setParcels(nextArticles)
    setFormData((prev) => ({ ...prev, ...syncFormFromBusArticles(nextArticles) }))
  }

  // Check if user is Agent role
  const roleName = user?.role || user?.role_name
  const isAgentRole = roleName === "Agent"
  const shipmentStatusKey = String(formData.status || "").trim().toLowerCase().replace(/[-\s]+/g, "_")
  const agentPartyOnlyStatuses = new Set(["dispatched", "received", "in_transit"])
  const agentRequestPartyOnly = isAgentRole && agentPartyOnlyStatuses.has(shipmentStatusKey)
  const agentCanEditToDestination = isAgentRole && shipmentStatusKey === "booked" && !agentRequestPartyOnly
  const agentMobileLastStep = agentRequestPartyOnly ? 2 : MOBILE_STEPS.length - 1

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)")
    const handler = (e) => setIsMobileLayout(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])

  const mobileStepPanelClass = (step) => {
    if (!isMobileLayout) return ""
    return mobileStep === step
      ? "flex flex-col flex-1 min-h-0 overflow-y-auto"
      : "hidden"
  }

  const validateMobileStep = (step) => {
    if (step === 0) {
      if (!formData.consigner_location?.trim()) {
        Alert.error("Please select a sender location")
        return false
      }
      if (!formData.consignee_location?.trim()) {
        Alert.error("Please select a receiver location")
        return false
      }
      return true
    }
    if (step === 1) {
      return validate(formData, {
        consigner_name: validationRules.consigner_name,
        consigner_contact: validationRules.consigner_contact,
      })
    }
    if (step === 2) {
      return validate(formData, {
        consignee_name: validationRules.consignee_name,
        consignee_contact: validationRules.consignee_contact,
      })
    }
    if (step === 3) {
      if (agentRequestPartyOnly) return true
      return validate(formData, {
        article_type: validationRules.article_type,
        no_of_articles: validationRules.no_of_articles,
        article_amount: validationRules.article_amount,
      })
    }
    return true
  }

  const goToNextMobileStep = () => {
    const step = mobileStepRef.current
    if (!validateMobileStep(step)) return
    const nextStep = Math.min(step + 1, MOBILE_STEPS.length - 1)
    mobileStepRef.current = nextStep
    setMobileStep(nextStep)
  }

  const handleFormSubmit = (e) => {
    e?.preventDefault?.()
    if (isMobileLayout) {
      const step = mobileStepRef.current
      if (step < agentMobileLastStep) {
        if (!validateMobileStep(step)) return
        const nextStep = step + 1
        mobileStepRef.current = nextStep
        setMobileStep(nextStep)
        return
      }
    }
    handleSubmit(e)
  }

  const handleMobilePrimaryAction = () => {
    if (mobileStepRef.current < agentMobileLastStep) {
      goToNextMobileStep()
      return
    }
    handleSubmit({ preventDefault: () => {} })
  }

  const handleMobileStepTabClick = (idx) => {
    const current = mobileStepRef.current
    if (idx === current) return
    if (idx < current) {
      mobileStepRef.current = idx
      setMobileStep(idx)
      return
    }
    for (let step = current; step < idx; step += 1) {
      if (!validateMobileStep(step)) return
    }
    mobileStepRef.current = idx
    setMobileStep(idx)
  }

  const goToPrevMobileStep = () => {
    setMobileStep((s) => Math.max(s - 1, 0))
  }

  useEffect(() => {
    fetchShipment()
    fetchLocationHubs()
    fetchShipmentClients()
    fetchCompanySettings()
    // Load this shipment once. The fetchers are recreated each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId])

  const fetchCompanySettings = async () => {
    try {
      const res = await companySettingsService.getSettings()
      if (res?.data?.rcm) {
        setCompanyRcm({ enabled: false, igst: 0, cgst: 0, sgst: 0, ...res.data.rcm })
      }
      setCompanyGstInfo({
        gst_number: res?.data?.company_gst_number || "",
        state: res?.data?.company_state || "",
      })
      if (typeof res?.data?.gst_verification === 'boolean') {
        setGstVerificationOn(res.data.gst_verification)
      }
      const insurance = res?.data?.shipment_charge_defaults?.insurance
        || res?.data?.shipment_insurance_charge
        || {}
      const defaults = resolveCargoDefaults(
        res?.data?.shipment_charge_defaults || res?.data?.cargo_charge_defaults,
      )
      defaults.insurance = {
        enabled: !!insurance.enabled,
        amount: Number.parseFloat(insurance.amount) || 0,
        mode: insurance.mode === 'percent' ? 'percent' : 'fixed',
      }
      setChargeDefaults(defaults)
      setLabourPolicy(normalizeLabourChargePolicy(res?.data?.labour_charge_policy))
      setBusFreightByWeight(!!res?.data?.bus_freight_by_weight)
      setShowAutoMlSection(
        !isExternalNetworkCompany(user) && res?.data?.is_auto_ml_enabled !== false
      )
    } catch (error) {
      // Settings unavailable -> keep manual GST / no insurance field
    }
  }

  const getHubState = (hubId, locationName = null) => {
    const list = [...consignerLocations, ...consigneeLocations]
    if (hubId) {
      const byId = list.find((h) => String(h.id) === String(hubId))
      if (byId?.state) return byId.state
    }
    if (locationName) {
      const name = locationName.trim().toLowerCase()
      const byName = list.find((h) => (h.location || "").trim().toLowerCase() === name)
      if (byName?.state) return byName.state
    }
    return null
  }

  const deriveRcmGst = () => deriveBookingGst({
    rcmEnabled: !!companyRcm.enabled,
    rcm: companyRcm,
    senderGst: formData.consigner_gst,
    receiverGst: formData.consignee_gst,
    mode: formData.mode_of_payment,
    companyGst: companyGstInfo.gst_number,
    senderState: getHubState(formData.sender_location_hub_id, formData.consigner_location),
    receiverState: getHubState(formData.receiver_location_hub_id, formData.consignee_location),
    verificationOn: gstVerificationOn,
    senderLookup: senderGstLookup,
    receiverLookup: receiverGstLookup,
  })

  const rcmActive = !gstExempt && deriveRcmGst().override

  useEffect(() => {
    if (!rcmActive) return
    const d = deriveRcmGst()
    const s = String(d.sgst)
    const c = String(d.cgst)
    const i = String(d.igst)
    setFormData((prev) => {
      if (
        prev.sgst_percentage === s &&
        prev.cgst_percentage === c &&
        prev.igst_percentage === i
      ) {
        return prev
      }
      return { ...prev, sgst_percentage: s, cgst_percentage: c, igst_percentage: i }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rcmActive,
    gstVerificationOn,
    senderGstLookup,
    receiverGstLookup,
    companyRcm,
    companyGstInfo,
    formData.consigner_gst,
    formData.consignee_gst,
    formData.mode_of_payment,
    formData.sender_location_hub_id,
    formData.receiver_location_hub_id,
    formData.consigner_location,
    formData.consignee_location,
    consignerLocations,
    consigneeLocations,
  ])

  useEffect(() => {
    const prev = prevModeOfPaymentRef.current
    prevModeOfPaymentRef.current = formData.mode_of_payment

    const isPaid =
      formData.mode_of_payment === "paid" || formData.mode_of_payment === "paid_balance"
    const wasPaid = prev === "paid" || prev === "paid_balance"

    if (isPaid && !wasPaid) {
      if (!isMobileLayout) {
        setShowParcelImages(true)
      }
      if (!isMobileLayout) {
        const timer = setTimeout(() => {
          paidPaymentFieldsRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          })
          setTimeout(() => {
            parcelImagesRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            })
          }, 350)
        }, 80)
        return () => clearTimeout(timer)
      }
    }
  }, [formData.mode_of_payment, isMobileLayout])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (consignerRef.current && !consignerRef.current.contains(event.target)) {
        setShowConsignerDropdown(false)
        setConsignerLocationSearch("")
      }
      if (consigneeRef.current && !consigneeRef.current.contains(event.target)) {
        setShowConsigneeDropdown(false)
      }
      if (bookingAgentDropdownRef.current && !bookingAgentDropdownRef.current.contains(event.target)) {
        setShowBookingAgentDropdown(false)
        setBookingAgentSearch("")
      }
      if (destinationAgentDropdownRef.current && !destinationAgentDropdownRef.current.contains(event.target)) {
        setShowDestinationAgentDropdown(false)
        setDestinationAgentSearch("")
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const filteredConsignerLocations = consignerLocations.filter((loc) => {
    const label = formatHubBranchLabel(loc).toLowerCase()
    const q = consignerLocationSearch.toLowerCase()
    return label.includes(q) || (loc.location || "").toLowerCase().includes(q)
  }).sort(compareLocationsByNumber)
  const filteredConsigneeLocations = consigneeLocations.filter((loc) => {
    const label = formatHubBranchLabel(loc).toLowerCase()
    const q = consigneeLocationSearch.toLowerCase()
    return label.includes(q) || (loc.location || "").toLowerCase().includes(q)
  }).sort(compareLocationsByNumber)
  const internalHubDispatchControls = hasInternalHubDispatchControls(user)
  const creditAllowed = !internalHubDispatchControls || bookingHub?.allow_on_account !== false
  const paymentStatusOptions = useMemo(() => {
    const options = internalHubDispatchControls
      ? paymentOptionsForHub(bookingHub)
      : [
        { value: "to_pay", label: "To Pay" },
        { value: "credit", label: "Credit" },
        { value: "paid", label: "Paid" },
      ]
    return options
  }, [internalHubDispatchControls, bookingHub])

  const canAdminEditPaymentStatus = roleName === "Admin" || roleName === "System Admin"
  const canAssignBookingAgent = canAdminEditPaymentStatus
  const canEditBookingAgent = canAssignBookingAgent && shipmentStatusKey === "booked"
  const senderBranchChanged = canAssignBookingAgent
    && String(formData.sender_location_hub_id || "") !== String(loadedSenderHubId || "")
  const lockAgentPicker = !senderBranchChanged && (!canEditBookingAgent || bookingAgents.length <= 1)
  const canSetDiscount = canAdminEditPaymentStatus
  const canAgentChangePaymentStatus =
    isAgentRole && agentAllowedFields.includes("mode_of_payment") && !agentRequestPartyOnly
  const canChangePaymentStatus = canAdminEditPaymentStatus || canAgentChangePaymentStatus
  const hasCreditClientSelected = Boolean(
    formData.shipment_client_id || selectedClient?.id,
  )
  const editPaymentStatusOptions = canAdminEditPaymentStatus
    ? [
        { value: "to_pay", label: "To Pay" },
        { value: "paid", label: "Paid" },
        { value: "credit", label: "Credit" },
      ]
    : paymentStatusOptions

  useEffect(() => {
    if (!canAssignBookingAgent) {
      setBookingAgents([])
      setBookingAgentUserId("")
      return
    }
    const hubId = formData.sender_location_hub_id
    if (!hubId) {
      setBookingAgents([])
      setBookingAgentUserId("")
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        setBookingAgentsLoading(true)
        const response = await shipmentService.getBookingAgents(hubId)
        if (cancelled) return
        if (response.data?.success === false) {
          throw new Error(response.data?.error || "Failed to load booking agents")
        }
        const agents = response.data?.data?.agents || []
        setBookingAgents(agents)
        setBookingAgentUserId((prev) => {
          if (prev && agents.some((a) => a.user_id === prev)) return prev
          if (shipmentOwnerUserId && agents.some((a) => a.user_id === shipmentOwnerUserId)) {
            return shipmentOwnerUserId
          }
          if (agents.length === 1) return agents[0].user_id
          return ""
        })
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load booking agents:", error)
          setBookingAgents([])
          setBookingAgentUserId("")
        }
      } finally {
        if (!cancelled) setBookingAgentsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [canAssignBookingAgent, formData.sender_location_hub_id, shipmentOwnerUserId])

  const destinationHubChanged = !isAgentRole
    && String(formData.receiver_location_hub_id || "") !== String(loadedReceiverHubId || "")
  const showDestinationAgentPicker = !isAgentRole
    && Boolean(formData.receiver_location_hub_id)
    && (destinationHubChanged || destinationAgentNeedsSelection)

  useEffect(() => {
    if (!showDestinationAgentPicker) {
      setDestinationAgents([])
      setDestinationAgentUserId("")
      return
    }
    const hubId = formData.receiver_location_hub_id
    let cancelled = false
    const load = async () => {
      try {
        setDestinationAgentsLoading(true)
        const response = await shipmentService.getDestinationReceivingAgents(hubId)
        if (cancelled) return
        if (response.data?.success === false) {
          throw new Error(response.data?.error || "Failed to load destination agents")
        }
        const agents = response.data?.agents || []
        setDestinationAgents(agents)
        setDestinationAgentUserId((prev) => {
          if (prev && agents.some((a) => a.user_id === prev)) return prev
          if (loadedReceiverAgentUserId && agents.some((a) => a.user_id === loadedReceiverAgentUserId)) {
            return loadedReceiverAgentUserId
          }
          if (agents.length === 1) return agents[0].user_id
          return ""
        })
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load destination agents:", error)
          setDestinationAgents([])
          setDestinationAgentUserId("")
        }
      } finally {
        if (!cancelled) setDestinationAgentsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [
    showDestinationAgentPicker,
    formData.receiver_location_hub_id,
    loadedReceiverAgentUserId,
  ])

  useEffect(() => {
    if (useShipmentClient) return
    if (canAdminEditPaymentStatus) return
    if (!internalHubDispatchControls || paymentStatusOptions.length === 0) return
    if (!paymentStatusOptions.some((option) => option.value === formData.mode_of_payment)) {
      setFormData((prev) => ({ ...prev, mode_of_payment: paymentStatusOptions[0].value }))
    }
  }, [internalHubDispatchControls, formData.mode_of_payment, paymentStatusOptions, formData.sender_location_hub_id, bookingHub?.id, bookingHub?.allow_to_pay, bookingHub?.allow_paid, bookingHub?.allow_on_account, useShipmentClient, canAdminEditPaymentStatus])

  useEffect(() => {
    if (!useShipmentClient) return
    if (canAdminEditPaymentStatus && formData.mode_of_payment !== "credit") return
    if (formData.mode_of_payment === "credit") return
    setFormData((prev) => ({
      ...prev,
      mode_of_payment: "credit",
      paid_amount: "",
      payment_mode: "",
    }))
  }, [useShipmentClient, formData.mode_of_payment, canAdminEditPaymentStatus])

  const filteredClients = shipmentClients.filter(
    (client) =>
      client.full_name.toLowerCase().includes(clientSearch.toLowerCase()) ||
      client.company_name.toLowerCase().includes(clientSearch.toLowerCase()),
  )

  const isValidEway = (value) => /^\d{12}$/.test(value)

  const handleCloseEwayBillModal = () => {
    setShowEwayBillModal(false)
    setEwayBills([""])
  }

  const handleSubmitEwayBills = async () => {
    const validBills = ewayBills.filter(isValidEway)
    if (validBills.length === 0) {
      Alert.error("Please enter at least one valid E-way bill")
      return
    }

    const allBills = [...existingEwayBills.map(b => b.eway_bill_number), ...submittedEwayBills, ...validBills]
    const uniqueBills = [...new Set(allBills)]

    if (uniqueBills.length !== allBills.length) {
      Alert.error("Duplicate E-way bill numbers found")
      return
    }

    setSubmittedEwayBills(prev => [...prev, ...validBills])
    Alert.success(`${validBills.length} E-way bill(s) added successfully`)
    handleCloseEwayBillModal()
  }

  const handleViewEwayBills = () => {
    const totalBills = existingEwayBills.length + submittedEwayBills.length
    if (totalBills === 0) {
      Alert.info("No E-way bills added yet")
      return
    }

    const existingNumbers = existingEwayBills.map(b => b.eway_bill_number).join(", ")
    const newNumbers = submittedEwayBills.join(", ")
    const message = `E-way Bills (${totalBills}):\n${existingNumbers}${newNumbers ? '\nNew: ' + newNumbers : ''}`

    Alert.info(message, { duration: 5000 })
  }

  const handleDeleteEwayBill = async (ewayBillId, ewayBillNumber) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete E-way bill ${ewayBillNumber}? This action cannot be undone.`,
      'Confirm Delete'
    )
    
    if (!confirmed) {
      return
    }
    
    try {
      setLoadingEwayBills(true)
      await shipmentService.deleteEwayBill(ewayBillId)
      Alert.success("E-way bill deleted successfully")
      await fetchEwayBills()
    } catch (error) {
      console.error("Error deleting e-way bill:", error)
      Alert.error(error.response?.data?.error || "Failed to delete E-way bill")
    } finally {
      setLoadingEwayBills(false)
    }
  }

  const fetchEwayBills = async () => {
    try {
      setLoadingEwayBills(true)
      const response = await shipmentService.getEwayBills(shipmentId)
      setExistingEwayBills(response.data.eway_bills || [])
    } catch (error) {
      console.error("Error fetching e-way bills:", error)
      setExistingEwayBills([])
    } finally {
      setLoadingEwayBills(false)
    }
  }

  const handleUpdateEwayVehicle = async () => {
    if (!selectedEwayBill?.id || !updateVehicleForm.vehicleNo) {
      Alert.error('Vehicle number is required')
      return
    }
    try {
      setEwayActionLoading(true)
      await shipmentService.updateEwayBillVehicle(selectedEwayBill.id, {
        vehicleNo: updateVehicleForm.vehicleNo.replace(/\s/g, '').toUpperCase(),
        reasonRem: updateVehicleForm.reasonRem || 'Vehicle Update',
      })
      Alert.success('Vehicle / Part-B updated')
      setShowUpdateVehicleModal(false)
      setSelectedEwayBill(null)
      await fetchEwayBills()
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to update vehicle')
    } finally {
      setEwayActionLoading(false)
    }
  }

  const handleCancelEwayBill = async (bill) => {
    const confirmed = await Alert.confirm(
      `Cancel e-way bill ${bill.eway_bill_number}? GST allows cancel only within ~24 hours of generation.`,
      'Confirm Cancel'
    )
    if (!confirmed) return
    try {
      setLoadingEwayBills(true)
      await shipmentService.cancelEwayBill(bill.id, {
        cancelRmrk: 'Order Cancelled',
        cancelRsnCode: 2,
      })
      Alert.success('E-way bill cancelled')
      await fetchEwayBills()
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to cancel e-way bill')
    } finally {
      setLoadingEwayBills(false)
    }
  }

  const fetchShipmentClients = async () => {
    try {
      const response = await shipmentClientService.getAll(1, 1000, { status: "active" })
      setShipmentClients(response.data || [])
    } catch (error) {
      console.error("Error fetching shipment clients:", error)
    }
  }

  const handleClientSelect = (client) => {
    setSelectedClient(client)
    setFormData({
      ...formData,
      consigner_name: client.full_name,
      consigner_address: client.address,
      consigner_contact: client.contact,
      consigner_email: client.email,
      consigner_gst: client.gst_number || "",
      shipment_client_id: client.id,
      mode_of_payment: "credit",
      paid_amount: "",
      payment_mode: "",
    })
    setShowClientDropdown(false)
    setClientSearch("")
    setClientHighlightIndex(-1)
  }

  const handleUseClientChange = (checked) => {
    setUseShipmentClient(checked)
    if (checked) {
      skipCreditRematchRef.current = ""
      setFormData((prev) => ({
        ...prev,
        mode_of_payment: "credit",
        paid_amount: "",
        payment_mode: "",
      }))
      return
    }
    skipCreditRematchRef.current = String(formData.consigner_contact || "").replace(/\D/g, "")
    setSelectedClient(null)
    setClientSearch("")
    setShowClientDropdown(false)
    setClientHighlightIndex(-1)
    setFormData((prev) => ({
      ...prev,
      shipment_client_id: "",
      ...(prev.mode_of_payment === "credit"
        ? { mode_of_payment: "to_pay", paid_amount: "", payment_mode: "" }
        : {}),
    }))
  }

  useEffect(() => {
    if (canAdminEditPaymentStatus && formData.mode_of_payment !== "credit") return
    if (useShipmentClient || selectedClient) return
    const digits = String(formData.consigner_contact || "").replace(/\D/g, "")
    if (digits.length !== 10) return
    if (skipCreditRematchRef.current === digits) return
    const match = shipmentClients.find(
      (client) => String(client.contact || "").replace(/\D/g, "") === digits,
    )
    if (!match) return
    setUseShipmentClient(true)
    handleClientSelect(match)
    // handleClientSelect is recreated each render; the guards above stop a repeat.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.consigner_contact, formData.mode_of_payment, shipmentClients, useShipmentClient, selectedClient, canAdminEditPaymentStatus])

  const fetchConsignorSuggestions = async (searchQuery) => {
    if (consignorDebounceRef.current) {
      clearTimeout(consignorDebounceRef.current)
    }

    const digits = String(searchQuery || "").replace(/\D/g, "")
    if (digits.length < 10) {
      setConsignorSuggestions([])
      setShowConsignorSuggestions(false)
      setConsignorSuggestionsLoading(false)
      return
    }

    setShowConsignorSuggestions(true)
    setConsignorSuggestionsLoading(true)

    consignorDebounceRef.current = setTimeout(async () => {
      try {
        const response = await shipmentService.getCustomerSuggestions(digits)
        setConsignorSuggestions(
          shipmentService.mergeCustomerSuggestions(
            response.data?.data?.senders,
            response.data?.data?.receivers,
          )
        )
      } catch (error) {
        console.error("Error fetching consignor suggestions:", error)
        setConsignorSuggestions([])
      } finally {
        setConsignorSuggestionsLoading(false)
      }
    }, 400)
  }

  const fetchConsigneeSuggestions = async (searchQuery) => {
    if (consigneeDebounceRef.current) {
      clearTimeout(consigneeDebounceRef.current)
    }

    const digits = String(searchQuery || "").replace(/\D/g, "")
    if (digits.length < 10) {
      setConsigneeSuggestions([])
      setShowConsigneeSuggestions(false)
      setConsigneeSuggestionsLoading(false)
      return
    }

    setShowConsigneeSuggestions(true)
    setConsigneeSuggestionsLoading(true)

    consigneeDebounceRef.current = setTimeout(async () => {
      try {
        const response = await shipmentService.getCustomerSuggestions(digits)
        const contactKey = digits.slice(-10)

        setFormData((prev) => {
          const senderKey = String(prev.consigner_contact || "").replace(/\D/g, "").slice(-10)
          const currentSender =
            senderKey === contactKey && (prev.consigner_name || prev.consigner_contact)
              ? [{
                  name: prev.consigner_name || "",
                  contact: prev.consigner_contact || "",
                  email: prev.consigner_email || "",
                  address: prev.consigner_address || "",
                  gst: prev.consigner_gst || "",
                }]
              : []

          setConsigneeSuggestions(
            shipmentService.mergeCustomerSuggestions(
              currentSender,
              response.data?.data?.receivers,
              response.data?.data?.senders,
            )
          )

          const consigneeNameEmpty = !String(prev.consignee_name || "").trim()
          if (senderKey === contactKey && consigneeNameEmpty && prev.consigner_name) {
            return {
              ...prev,
              consignee_name: prev.consigner_name || "",
              consignee_contact: prev.consigner_contact || contactKey,
              consignee_address: prev.consigner_address || "",
              consignee_email: prev.consigner_email || "",
              consignee_gst: prev.consigner_gst || "",
            }
          }
          return prev
        })
      } catch (error) {
        console.error("Error fetching consignee suggestions:", error)
        setConsigneeSuggestions([])
      } finally {
        setConsigneeSuggestionsLoading(false)
      }
    }, 400)
  }

  const handleConsignorSuggestionSelect = (suggestion) => {
    setFormData((prev) => {
      const consigneeEmpty =
        !String(prev.consignee_contact || "").trim() &&
        !String(prev.consignee_name || "").trim()

      const next = {
        ...prev,
        consigner_name: suggestion.name || "",
        consigner_contact: suggestion.contact || "",
        consigner_address: suggestion.address || "",
        consigner_email: suggestion.email || "",
        consigner_gst: suggestion.gst || "",
      }

      if (consigneeEmpty) {
        next.consignee_name = suggestion.name || ""
        next.consignee_contact = suggestion.contact || ""
        next.consignee_address = suggestion.address || ""
        next.consignee_email = suggestion.email || ""
        next.consignee_gst = suggestion.gst || ""
      }
      return next
    })
    setShowConsignorSuggestions(false)
    setConsignorSuggestions([])
    setConsignorSuggestionHighlightIndex(-1)
  }

  const handleConsigneeSuggestionSelect = (suggestion) => {
    setFormData({
      ...formData,
      consignee_name: suggestion.name || "",
      consignee_contact: suggestion.contact || "",
      consignee_address: suggestion.address || "",
      consignee_email: suggestion.email || "",
      consignee_gst: suggestion.gst || "",
    })
    setShowConsigneeSuggestions(false)
    setConsigneeSuggestions([])
    setConsigneeSuggestionHighlightIndex(-1)
  }

  const fetchShipment = async () => {
    try {
      setLoading(true)
      const [shipmentResponse, paymentHistoryResponse] = await Promise.all([
        shipmentService.getShipment(shipmentId),
        paymentService.getPaymentHistory(shipmentId).catch(() => null),
      ])
      const shipment = shipmentResponse.data.shipment || shipmentResponse.data.data
      if (shipment?.is_deleted) {
        Alert.error('This shipment is deleted and cannot be edited.')
        navigate(`/shipments/view/${shipmentId}`)
        return
      }
      if (!isAgentRole && shipment?.can_edit === false) {
        Alert.error(shipment.edit_block_reason || 'This shipment is on an ML sheet. Only Admin can edit it.')
        navigate(`/shipments/view/${shipmentId}`)
        return
      }
      const paymentHistory =
        paymentHistoryResponse?.data?.data?.payment_history
        || paymentHistoryResponse?.data?.payment_history
        || []

      const mappedShipment = {
        ...shipment,
        consigner_name: shipment.consigner_name || shipment.sender_name,
        consigner_address: shipment.consigner_address || shipment.sender_address,
        consigner_location: shipment.consigner_location || shipment.sender_location,
        sender_location_hub_id: shipment.sender_location_hub_id || "",
        consigner_contact: shipment.consigner_contact || shipment.sender_contact,
        consigner_email: shipment.consigner_email || shipment.sender_email,
        consigner_gst: shipment.consigner_gst || shipment.sender_gst,
        consignee_name: shipment.consignee_name || shipment.receiver_name,
        consignee_address: shipment.consignee_address || shipment.receiver_address,
        consignee_location: shipment.consignee_location || shipment.receiver_location,
        receiver_location_hub_id: shipment.receiver_location_hub_id || "",
        consignee_contact: shipment.consignee_contact || shipment.receiver_contact,
        consignee_email: shipment.consignee_email || shipment.receiver_email,
        consignee_gst: shipment.consignee_gst || shipment.receiver_gst,
        invoice_number: shipment.invoice_number || "",
        invoice_value: shipment.invoice_value ?? "",
        booking_date: shipment.created_at ? shipment.created_at.split("T")[0] : getLocalDateString(),
        payment_mode: resolveShipmentPaymentMode(shipment, paymentHistory),
        paid_amount: shipment.paid_amount ?? "",
        insurance_charges: shipment.insurance_charges ?? "",
        rate_per_kg: shipment.rate_per_kg ?? "",
        loading_charges: shipment.loading_charges ?? "",
        unloading_charges: shipment.unloading_charges ?? "",
        toll_charges: shipment.toll_charges ?? "",
        fuel_surcharge: shipment.fuel_surcharge ?? "",
        packing_charges: shipment.packing_charges ?? "",
      }

      prevModeOfPaymentRef.current = shipment.mode_of_payment || "to_pay"

      const cargoShipment = (shipment.shipment_type || "bus") === "cargo"
      setIsCargoMode(cargoShipment)
      const loadedParcels = cargoShipment
        ? parcelsFromApi(shipment.parcels)
        : busArticlesFromApi(shipment.parcels, shipment)
      const parcelQty = sumParcelQty(loadedParcels)
      setParcels(loadedParcels)

      const toUnitRate = (stored, chargeKey) => {
        if (!PER_PARCEL_CHARGE_KEYS.has(chargeKey)) return stored ?? ''
        const unit = perParcelUnitRate(stored, parcelQty)
        return unit > 0 ? String(unit) : (stored ?? '')
      }

      setBulkLoad(!!shipment.bulk_load)
      setFormData({
        ...mappedShipment,
        loading_charges: toUnitRate(shipment.loading_charges, 'loading'),
        unloading_charges: toUnitRate(shipment.unloading_charges, 'unloading'),
        door_pickup_charges: toUnitRate(shipment.door_pickup_charges, 'door_pickup'),
        delivery_charges: toUnitRate(shipment.delivery_charges, 'door_delivery'),
        docket_fee: shipment.docket_fee != null ? String(shipment.docket_fee) : "",
        handling_charges: shipment.handling_charges != null ? String(shipment.handling_charges) : "",
        late_charges: shipment.late_charges != null ? String(shipment.late_charges) : "",
        other_charges: shipment.other_charges != null ? String(shipment.other_charges) : "",
        door_pickup_vehicle: shipment.door_pickup_vehicle === 'hired' ? 'hired' : 'own',
        door_delivery_vehicle: shipment.door_delivery_vehicle === 'hired' ? 'hired' : 'own',
      })
      setShipmentOwnerUserId(shipment.user_id || "")
      setLoadedSenderHubId(shipment.sender_location_hub_id ? String(shipment.sender_location_hub_id) : "")
      setLoadedReceiverHubId(shipment.receiver_location_hub_id ? String(shipment.receiver_location_hub_id) : "")
      setLoadedReceiverAgentUserId(shipment.receiver_agent_user_id || "")
      setDestinationAgentNeedsSelection(Boolean(shipment.destination_agent_needs_selection))

      // Store shipment ID type for display badge
      setShipmentIdType(shipment.shipment_id_type || 'auto')
      setConsignmentNo(shipment.consignment_no || null)

      // Store original charges for Agent role validation
      setOriginalCharges({
        handling_charges: shipment.handling_charges ?? "",
        delivery_charges: shipment.delivery_charges ?? "",
        door_pickup_charges: shipment.door_pickup_charges ?? "",
        late_charges: shipment.late_charges ?? "",
        docket_fee: shipment.docket_fee ?? "",
        other_charges: shipment.other_charges ?? "",
        discount: shipment.discount ?? "",
      })

      const mediaList = shipment.images || shipment.media || []
      if (mediaList.length) {
        setExistingMedia(mediaList)
      } else {
        const legacy = []
        if (shipment.parcel_image_1_url) {
          legacy.push({ id: null, media_type: 'photo', cdn_url: shipment.parcel_image_1_url })
        }
        if (shipment.parcel_image_2_url) {
          legacy.push({ id: null, media_type: 'photo', cdn_url: shipment.parcel_image_2_url })
        }
        setExistingMedia(legacy)
      }
      setRemovedMediaIds([])
      setShipmentMediaFiles([])

      if (isAgentRole) {
        try {
          const allowedRes = await shipmentEditRequestService.allowedFields(shipmentId)
          setAgentAllowedFields(allowedRes.data?.data?.allowed_fields || [])
        } catch (allowedErr) {
          Alert.error(allowedErr.response?.data?.error || "This shipment cannot be updated")
          navigate(`/shipments/view/${shipmentId}`)
        }
      } else {
        setAgentAllowedFields([])
      }
    } catch (error) {
      console.error("Error fetching shipment:", error)
      Alert.error("Failed to load shipment")
      navigate("/shipments")
    } finally {
      setLoading(false)
    }

    fetchEwayBills()
  }

  const fetchLocationHubs = async () => {
    try {
      const response = await shipmentService.getAllLocationHubs()
      const allLocations = filterShipmentLocations(response.data.hubs || [])
      setConsignerLocations(allLocations)
      setConsigneeLocations(filterBranchLocations(allLocations))
    } catch (error) {
      console.error("Error fetching location hubs:", error)
    }
  }

  const calculateFreightCharges = () => {
    if (chargeSplitMode) {
      return Number(chargeSplit.freight || 0).toFixed(2)
    }
    if (isCargoMode) {
      const totalChargeable = sumChargeableWeight(parcels)
      return calcFreightFromRate(toNum(formData.rate_per_kg), totalChargeable).toFixed(2)
    }
    return calcBusFreight(parcels, formData.rate_per_kg, busFreightByWeight).toFixed(2)
  }

  // Live % extras (same as Add Shipment) so edit recalculates loading/unloading/freight.
  useEffect(() => {
    if (chargeSplitMode) setShowAdditionalCharges(true)
  }, [chargeSplitMode])

  useEffect(() => {
    if (!chargeSplitMode) return
    setFormData((prev) => {
      let changed = false
      const next = { ...prev }
      EXTRA_SHIPMENT_CHARGE_KEYS.forEach((key) => {
        if (!chargeDefaults[key]?.enabled) return
        if (normalizeChargeMode(chargeDefaults[key].mode) !== 'percent') return
        if (key === 'loading' && !loadingAtBooking) return
        if (key === 'unloading' && !unloadingAtBooking) return
        const field = EXTRA_CHARGE_FORM_FIELDS[key]
        const val = roundPaise(chargeSplit.extras[key] ?? 0).toFixed(2)
        if (next[field] !== val) {
          next[field] = val
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [chargeSplitMode, chargeSplit, chargeDefaults, loadingAtBooking, unloadingAtBooking])

  useEffect(() => {
    if (!consignerLocations.length) return
    if (!showBulkLoadOption && bulkLoad) setBulkLoad(false)
  }, [consignerLocations.length, showBulkLoadOption, bulkLoad])

  useEffect(() => {
    if (!bulkLoadActive) return
    setFormData((prev) => (
      prev.loading_charges === '0.00' && prev.unloading_charges === '0.00'
        ? prev
        : { ...prev, loading_charges: '0.00', unloading_charges: '0.00' }
    ))
  }, [bulkLoadActive])

  // Keep paid_amount in sync when status is paid and total changes (same as Add).
  useEffect(() => {
    if (formData.mode_of_payment !== 'paid') return
    const total = calculateTotalAmount()
    if (String(formData.paid_amount) === String(total)) return
    setFormData((prev) => (
      prev.mode_of_payment === 'paid' && String(prev.paid_amount) !== String(total)
        ? { ...prev, paid_amount: total }
        : prev
    ))
    // calculateTotalAmount is declared below this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.mode_of_payment,
    formData.freight_charges,
    formData.handling_charges,
    formData.delivery_charges,
    formData.door_pickup_charges,
    formData.other_charges,
    formData.docket_fee,
    formData.discount,
    formData.sgst_percentage,
    formData.cgst_percentage,
    formData.igst_percentage,
    formData.no_of_articles,
    formData.article_amount,
    formData.rate_per_kg,
    formData.loading_charges,
    formData.unloading_charges,
    formData.toll_charges,
    formData.fuel_surcharge,
    formData.packing_charges,
    formData.insurance_charges,
    parcels,
    isCargoMode,
    chargeSplitMode,
    chargeSplit.freight,
    articleFreightBase,
  ])

  // Percent insurance: update when freight changes; keep loaded value on first run
  useEffect(() => {
    if (!chargeDefaults.insurance?.enabled) return
    if (chargeDefaults.insurance?.mode !== 'percent') return
    const freight = Number.parseFloat(calculateFreightCharges()) || 0
    if (insuranceFreightSyncedRef.current === null) {
      insuranceFreightSyncedRef.current = freight
      return
    }
    if (insuranceFreightSyncedRef.current === freight) return
    insuranceFreightSyncedRef.current = freight
    const next = String(calcInsuranceFromSettings(chargeDefaults.insurance, freight))
    setFormData((prev) => (
      prev.insurance_charges === next ? prev : { ...prev, insurance_charges: next }
    ))
    // calculateFreightCharges is declared in this component and follows the freight fields below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chargeDefaults.insurance?.enabled,
    chargeDefaults.insurance?.mode,
    chargeDefaults.insurance?.amount,
    parcels,
    formData.rate_per_kg,
    isCargoMode,
    busFreightByWeight,
    chargeSplitMode,
    chargeSplit.freight,
  ])

  const sharedExtraChargesTotal = () => {
    let total = 0
    if (bulkLoadActive) {
      if (chargeDefaults.toll.enabled || toNum(formData.toll_charges) > 0) total += toNum(formData.toll_charges)
      if (chargeDefaults.fuel_surcharge.enabled || toNum(formData.fuel_surcharge) > 0) total += toNum(formData.fuel_surcharge)
      if (chargeDefaults.packing.enabled || toNum(formData.packing_charges) > 0) total += toNum(formData.packing_charges)
      return total
    }
    if ((loadingAtBooking && (chargeDefaults.loading.enabled || toNum(formData.loading_charges) > 0))) {
      total += resolveChargeAmount('loading', 'loading_charges')
    } else if (!loadingAtBooking && toNum(formData.loading_charges) > 0) {
      total += resolveChargeAmount('loading', 'loading_charges')
    }
    if ((unloadingAtBooking && (chargeDefaults.unloading?.enabled || toNum(formData.unloading_charges) > 0))
      || (!unloadingAtBooking && toNum(formData.unloading_charges) > 0)) {
      total += resolveChargeAmount('unloading', 'unloading_charges')
    }
    if (chargeDefaults.toll.enabled || toNum(formData.toll_charges) > 0) total += toNum(formData.toll_charges)
    if (chargeDefaults.fuel_surcharge.enabled || toNum(formData.fuel_surcharge) > 0) total += toNum(formData.fuel_surcharge)
    if (chargeDefaults.packing.enabled || toNum(formData.packing_charges) > 0) total += toNum(formData.packing_charges)
    return total
  }

  const roundRupee = (value) => roundPaise(value)

  const calculateTotalAmount = () => {
    const freightCharges = Number.parseFloat(calculateFreightCharges()) || 0
    const handlingCharges = (chargeDefaults.handling?.enabled || toNum(formData.handling_charges) > 0)
      ? (Number.parseFloat(formData.handling_charges) || 0)
      : 0
    const deliveryCharges = (chargeDefaults.door_delivery?.enabled || toNum(formData.delivery_charges) > 0)
      ? resolveChargeAmount('door_delivery', 'delivery_charges')
      : 0
    const doorPickupCharges = (chargeDefaults.door_pickup?.enabled || toNum(formData.door_pickup_charges) > 0)
      ? resolveChargeAmount('door_pickup', 'door_pickup_charges')
      : 0
    const docketFee = (chargeDefaults.docket?.enabled || toNum(formData.docket_fee) > 0)
      ? (Number.parseFloat(formData.docket_fee) || 0)
      : 0
    const lateCharges = Number.parseFloat(formData.late_charges) || 0
    const otherCharges = Number.parseFloat(formData.other_charges) || 0
    const insuranceCharges = chargeDefaults.insurance?.enabled
      ? (Number.parseFloat(formData.insurance_charges) || 0)
      : 0
    const discount = Number.parseFloat(formData.discount) || 0
    const sharedExtra = sharedExtraChargesTotal()

    const subtotal = roundPaise(Math.max(
      0,
      freightCharges + handlingCharges + deliveryCharges + doorPickupCharges + docketFee + lateCharges + otherCharges + insuranceCharges + sharedExtra - discount,
    ))
    const bookingGst = deriveRcmGst()
    const gstBase = bookingGst.override ? roundPaise(Math.max(0, freightCharges)) : subtotal

    let sgstPct = 0
    let cgstPct = 0
    let igstPct = 0
    let includeGstInTotal = true
    if (!gstExempt) {
      if (bookingGst.override) {
        sgstPct = Number(bookingGst.sgst) || 0
        cgstPct = Number(bookingGst.cgst) || 0
        igstPct = Number(bookingGst.igst) || 0
        includeGstInTotal = bookingGst.includeInTotal !== false
      } else {
        sgstPct = Number.parseFloat(formData.sgst_percentage) || 0
        cgstPct = Number.parseFloat(formData.cgst_percentage) || 0
        igstPct = Number.parseFloat(formData.igst_percentage) || 0
      }
    }

    const sgst = roundRupee((gstBase * sgstPct) / 100)
    const cgst = roundRupee((gstBase * cgstPct) / 100)
    const igst = roundRupee((gstBase * igstPct) / 100)

    const payable = subtotal + (includeGstInTotal ? sgst + cgst + igst : 0)
    return roundPaise(payable).toFixed(2)
  }

  const calculateGSTAmounts = () => {
    const freightCharges = Number.parseFloat(calculateFreightCharges()) || 0
    const handlingCharges = (chargeDefaults.handling?.enabled || toNum(formData.handling_charges) > 0)
      ? (Number.parseFloat(formData.handling_charges) || 0)
      : 0
    const deliveryCharges = (chargeDefaults.door_delivery?.enabled || toNum(formData.delivery_charges) > 0)
      ? resolveChargeAmount('door_delivery', 'delivery_charges')
      : 0
    const doorPickupCharges = (chargeDefaults.door_pickup?.enabled || toNum(formData.door_pickup_charges) > 0)
      ? resolveChargeAmount('door_pickup', 'door_pickup_charges')
      : 0
    const docketFee = (chargeDefaults.docket?.enabled || toNum(formData.docket_fee) > 0)
      ? (Number.parseFloat(formData.docket_fee) || 0)
      : 0
    const lateCharges = Number.parseFloat(formData.late_charges) || 0
    const otherCharges = Number.parseFloat(formData.other_charges) || 0
    const insuranceCharges = chargeDefaults.insurance?.enabled
      ? (Number.parseFloat(formData.insurance_charges) || 0)
      : 0
    const discount = Number.parseFloat(formData.discount) || 0
    const sharedExtra = sharedExtraChargesTotal()

    const subtotal = roundPaise(Math.max(
      0,
      freightCharges + handlingCharges + deliveryCharges + doorPickupCharges + docketFee + lateCharges + otherCharges + insuranceCharges + sharedExtra - discount,
    ))
    const bookingGst = deriveRcmGst()
    const gstBase = bookingGst.override ? roundPaise(Math.max(0, freightCharges)) : subtotal

    let sgstPct = 0
    let cgstPct = 0
    let igstPct = 0
    if (!gstExempt) {
      if (bookingGst.override) {
        sgstPct = Number(bookingGst.sgst) || 0
        cgstPct = Number(bookingGst.cgst) || 0
        igstPct = Number(bookingGst.igst) || 0
      } else {
        sgstPct = Number.parseFloat(formData.sgst_percentage) || 0
        cgstPct = Number.parseFloat(formData.cgst_percentage) || 0
        igstPct = Number.parseFloat(formData.igst_percentage) || 0
      }
    }

    return {
      sgst: roundPaise((gstBase * sgstPct) / 100).toFixed(2),
      cgst: roundPaise((gstBase * cgstPct) / 100).toFixed(2),
      igst: roundPaise((gstBase * igstPct) / 100).toFixed(2),
    }
  }

  const calculateBalanceAmount = () => {
    const totalAmount = Number.parseFloat(calculateTotalAmount())
    const paid = Number.parseFloat(formData.paid_amount) || 0
    return roundPaise(totalAmount - paid).toFixed(2)
  }

  const handleEnterAsTab = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      const form = e.target.form
      if (!form) return
      const elements = Array.from(
        form.querySelectorAll(
          "input:not([type=hidden]):not([disabled]):not([type=checkbox]):not([type=radio]), select:not([disabled]), textarea:not([disabled])"
        )
      ).filter((el) => el.offsetParent !== null)
      const submitBtn =
        !isMobileLayout || mobileStepRef.current >= MOBILE_STEPS.length - 1
          ? document.getElementById("edit-shipment-submit") ||
            document.getElementById("edit-shipment-submit-mobile")
          : null
      if (submitBtn) elements.push(submitBtn)
      const index = elements.indexOf(e.target)
      if (index > -1 && index + 1 < elements.length) {
        elements[index + 1].focus()
      }
    }
  }

  const handleLocationDropdownKeyDown = (e, config) =>
    handleSuggestionDropdownKeyDown(e, { ...config, onEnterFallback: handleEnterAsTab })

  const selectFromBranch = (loc) => {
    if (
      String(loc?.id ?? "") === String(formData.receiver_location_hub_id)
      && formData.receiver_location_hub_id !== ""
    ) {
      Alert.error("Sender and Receiver locations cannot be the same")
      return
    }
    setFormData((prev) => ({
      ...prev,
      consigner_location: formatHubBranchLabel(loc),
      sender_location_hub_id: loc?.id ?? "",
    }))
    setConsignerLocationSearch("")
    setShowConsignerDropdown(false)
    setConsignerHighlightIndex(-1)
  }

  const agentChargeFieldVisible = (fieldName) => {
    switch (fieldName) {
      case 'handling_charges':
        return chargeDefaults.handling?.enabled || toNum(formData.handling_charges) > 0
      case 'delivery_charges':
        return chargeDefaults.door_delivery?.enabled || toNum(formData.delivery_charges) > 0
      case 'door_pickup_charges':
        return chargeDefaults.door_pickup?.enabled || toNum(formData.door_pickup_charges) > 0
      case 'docket_fee':
        return chargeDefaults.docket?.enabled || toNum(formData.docket_fee) > 0
      case 'late_charges':
      case 'other_charges':
      case 'discount':
        return true
      default:
        return false
    }
  }

  const storedOriginalCharge = (fieldName) => Number.parseFloat(originalCharges[fieldName]) || 0

  // Validate charge reduction for Agent role (only fields shown on the form)
  const validateAgentChargeReduction = (fieldName, newValue) => {
    if (!isAgentRole) return true
    if (!agentChargeFieldVisible(fieldName)) return true

    const originalValue = storedOriginalCharge(fieldName)
    const newVal = Number.parseFloat(newValue) || 0

    if (newVal < originalValue) {
      const label = fieldName.replace(/_/g, ' ')
      Alert.error(`As an Agent, you cannot reduce ${label}. You can only keep it the same or increase it.`)
      return false
    }
    return true
  }

  const handleChange = (e) => {
    const { name, value, type } = e.target
    if (name === "discount" && !canSetDiscount) return

    const chargeFields = [
      "freight_charges",
      "handling_charges",
      "delivery_charges",
      "door_pickup_charges",
      "late_charges",
      "docket_fee",
      "other_charges",
      "insurance_charges",
      "discount",
      "paid_amount",
      "article_amount",
      "rate_per_kg",
      "loading_charges",
      "unloading_charges",
      "toll_charges",
      "fuel_surcharge",
      "packing_charges",
      "sgst_percentage",
      "cgst_percentage",
      "igst_percentage",
    ]

    if (name === "article_type") {
      if (value === "OTHER") {
        setFormData((prev) => ({ ...prev, article_type: "" }))
        return
      }
      setFormData((prev) => ({ ...prev, article_type: value.toUpperCase() }))
      return
    }

    if (chargeFields.includes(name)) {
      if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
        // Validate Agent role restrictions on charge reduction
        const restrictedCharges = [
          "handling_charges",
          "delivery_charges",
          "door_pickup_charges",
          "late_charges",
          "docket_fee",
          "other_charges",
        ]

        if (restrictedCharges.includes(name) && !validateAgentChargeReduction(name, value)) {
          return
        }

        // Validate discount increase for Agent
        if (name === "discount" && !validateAgentChargeReduction(name, value)) {
          return
        }

        setFormData({ ...formData, [name]: value })
        if (validationRules[name]) validateField(name, value, validationRules[name])
      }
    } else {
      let filtered = value
      if (name === 'consigner_contact' || name === 'consignee_contact') filtered = inputFilters.digits10(value)
      if (name === 'invoice_value') filtered = String(value).replace(/\D/g, '').slice(0, 12)
      if (name === 'consigner_gst' || name === 'consignee_gst') {
        filtered = inputFilters.uppercase(value).replace(/[^A-Z0-9]/g, '').slice(0, 15)
      }
      
      const updatedFormData = {
        ...formData,
        [name]: type === "number" ? (filtered === "" ? "" : Number.parseFloat(filtered)) : filtered,
      }

      if (name === "mode_of_payment") {
        if (value === "to_pay") {
          updatedFormData.paid_amount = ""
          updatedFormData.payment_mode = ""
          updatedFormData.shipment_client_id = ""
          setUseShipmentClient(false)
          setSelectedClient(null)
        } else if (value === "credit") {
          updatedFormData.paid_amount = ""
          updatedFormData.payment_mode = ""
          setUseShipmentClient(true)
        } else if (value === "paid") {
          updatedFormData.payment_mode = "cash"
          updatedFormData.paid_amount = calculateTotalAmount()
          updatedFormData.shipment_client_id = ""
          setUseShipmentClient(false)
          setSelectedClient(null)
        } else if (value === "paid_balance") {
          updatedFormData.payment_mode = "cash"
          updatedFormData.paid_amount = ""
        }
      }

      setFormData(updatedFormData)
      if (validationRules[name]) validateField(name, updatedFormData[name], validationRules[name])
    }
  }

  const handleSubmit = async (e) => {
    e?.preventDefault?.()
    if (!validate(formData, validationRules)) return
    if (senderBranchChanged) {
      if (bookingAgentsLoading) {
        Alert.error("Wait for the agents at this branch to load.")
        return
      }
      if (!bookingAgents.length) {
        Alert.error("This branch has no active agent. The from location cannot be changed until an agent is assigned there.")
        return
      }
      if (!bookingAgentUserId) {
        Alert.error("Select the agent at this branch before saving the from location.")
        return
      }
    } else if (canEditBookingAgent && bookingAgents.length > 1 && !bookingAgentUserId) {
      Alert.error("This branch has multiple agents. Please select the booking agent.")
      return
    }
    if (showDestinationAgentPicker) {
      if (destinationAgentsLoading) {
        Alert.error("Wait for the agents at the destination to load.")
        return
      }
      if (!destinationAgents.length) {
        Alert.error("This destination has no active agent. The parcel cannot be moved there until an agent is assigned.")
        return
      }
      if (!destinationAgentUserId) {
        Alert.error("Select the agent at the new destination. That agent will receive and deliver this parcel.")
        return
      }
    }
    try {
      setSaving(true)

      // Final validation for Agent role
      if (isAgentRole) {
        const restrictedCharges = {
          handling_charges: originalCharges.handling_charges,
          delivery_charges: originalCharges.delivery_charges,
          door_pickup_charges: originalCharges.door_pickup_charges,
          late_charges: originalCharges.late_charges,
          docket_fee: originalCharges.docket_fee,
          other_charges: originalCharges.other_charges,
          discount: originalCharges.discount,
        }

        for (const [field, originalValue] of Object.entries(restrictedCharges)) {
          if (!agentChargeFieldVisible(field)) continue
          const currentValue = Number.parseFloat(formData[field]) || 0
          const original = Number.parseFloat(originalValue) || 0
          if (currentValue < original) {
            Alert.error(`Agent role cannot reduce ${field.replace(/_/g, ' ')}`)
            setSaving(false)
            return
          }
        }
      }

      const invoiceValue = Number.parseInt(formData.invoice_value, 10) || 0
      const hasEwayBill = existingEwayBills.some((bill) => bill.status !== "cancelled") || submittedEwayBills.length > 0
      if (invoiceValue > EWAY_BILL_MANDATORY_VALUE && !hasEwayBill) {
        Alert.error(`E-way bill number is required when invoice value is more than ${EWAY_BILL_MANDATORY_LABEL}`)
        setSaving(false)
        return
      }

      if (formData.mode_of_payment === "credit" && !formData.shipment_client_id) {
        Alert.error("Select a credit client before requesting Credit payment status")
        setSaving(false)
        return
      }

      if (isAgentRole) {
        if (shipmentStatusKey === "booked" && !isCargoMode) {
          const articleError = validateBusArticles(parcels, { freightByWeight: busFreightByWeight })
          if (articleError) {
            Alert.error(articleError)
            setSaving(false)
            return
          }
        }
        const proposed = buildAgentProposedFromEditForm(formData, agentAllowedFields)
        if (shipmentStatusKey === "booked") {
          proposed.parcels = parcelsForApi(parcels)
        }
        const response = await shipmentEditRequestService.create({
          shipment_id: shipmentId,
          proposed_snapshot: proposed,
          remark: formData.agent_edit_remark || "",
        })
        Alert.success(response.data?.message || "Update request sent. Please wait for admin or accountant approval.")
        navigate("/shipment-edit-requests")
        return
      }

      if (!isCargoMode) {
        const articleError = validateBusArticles(parcels, { freightByWeight: busFreightByWeight })
        if (articleError) {
          Alert.error(articleError)
          setSaving(false)
          return
        }
      }

      const busSync = isCargoMode ? null : syncFormFromBusArticles(parcels)
      const { status: _lockedStatus, ...editableForm } = formData
      // The loaded shipment also carries sender_*/receiver_*. The inputs edit
      // consigner_*/consignee_*, so copy those onto the columns the API stores.
      const submitData = {
        ...editableForm,
        sender_name: formData.consigner_name || "",
        sender_address: formData.consigner_address ?? "",
        sender_contact: formData.consigner_contact || "",
        sender_email: formData.consigner_email ?? "",
        sender_gst: formData.consigner_gst ?? "",
        sender_location: formData.consigner_location || "",
        receiver_name: formData.consignee_name || "",
        receiver_address: formData.consignee_address ?? "",
        receiver_contact: formData.consignee_contact || "",
        receiver_email: formData.consignee_email ?? "",
        receiver_gst: formData.consignee_gst ?? "",
        receiver_location: formData.consignee_location || "",
        eway_bill_numbers: submittedEwayBills.length > 0 ? submittedEwayBills : undefined,
        created_at: formData.booking_date,
        shipment_type: isCargoMode ? "cargo" : "bus",
        no_of_articles: isCargoMode ? sumParcelQty(parcels) : busSync.no_of_articles,
        article_amount: isCargoMode ? 0 : (busFreightByWeight ? 0 : busSync.article_amount),
        article_type: isCargoMode ? (formData.article_type || "PALLET") : busSync.article_type,
        weight: isCargoMode
          ? sumChargeableWeight(parcels)
          : (busFreightByWeight ? sumBusArticleWeight(parcels) : formData.weight),
        freight_charges: Number.parseFloat(calculateFreightCharges()) || 0,
        handling_charges: (chargeDefaults.handling?.enabled || toNum(formData.handling_charges) > 0)
          ? (Number.parseFloat(formData.handling_charges) || 0)
          : storedOriginalCharge('handling_charges'),
        delivery_charges: (chargeDefaults.door_delivery?.enabled || toNum(formData.delivery_charges) > 0)
          ? resolveChargeAmount('door_delivery', 'delivery_charges')
          : storedOriginalCharge('delivery_charges'),
        door_pickup_charges: (chargeDefaults.door_pickup?.enabled || toNum(formData.door_pickup_charges) > 0)
          ? resolveChargeAmount('door_pickup', 'door_pickup_charges')
          : storedOriginalCharge('door_pickup_charges'),
        door_pickup_vehicle: (chargeDefaults.door_pickup?.enabled || toNum(formData.door_pickup_charges) > 0)
          ? (formData.door_pickup_vehicle === 'hired' ? 'hired' : 'own')
          : '',
        door_delivery_vehicle: (chargeDefaults.door_delivery?.enabled || toNum(formData.delivery_charges) > 0)
          ? (formData.door_delivery_vehicle === 'hired' ? 'hired' : 'own')
          : '',
        late_charges: Number.parseFloat(formData.late_charges) || 0,
        docket_fee: (chargeDefaults.docket?.enabled || toNum(formData.docket_fee) > 0)
          ? (Number.parseFloat(formData.docket_fee) || 0)
          : storedOriginalCharge('docket_fee'),
        other_charges: Number.parseFloat(formData.other_charges) || 0,
        insurance_charges: chargeDefaults.insurance?.enabled
          ? (Number.parseFloat(formData.insurance_charges) || 0)
          : 0,
        discount: canSetDiscount ? (Number.parseFloat(formData.discount) || 0) : (Number.parseFloat(originalCharges.discount) || 0),
        rate_per_kg: (isCargoMode || busFreightByWeight) ? toNum(formData.rate_per_kg) : 0,
        loading_charges: bulkLoadActive
          ? 0
          : (loadingAtBooking
            ? resolveChargeAmount('loading', 'loading_charges')
            : toNum(formData.loading_charges)),
        unloading_charges: bulkLoadActive
          ? 0
          : (unloadingAtBooking
            ? resolveChargeAmount('unloading', 'unloading_charges')
            : toNum(formData.unloading_charges)),
        bulk_load: bulkLoadActive,
        toll_charges: toNum(formData.toll_charges),
        fuel_surcharge: toNum(formData.fuel_surcharge),
        packing_charges: toNum(formData.packing_charges),
        parcels: parcelsForApi(parcels),
        sgst_percentage: gstExempt
          ? 0
          : deriveRcmGst().override
          ? (Number(deriveRcmGst().sgst) || 0)
          : (Number.parseFloat(formData.sgst_percentage) || 0),
        cgst_percentage: gstExempt
          ? 0
          : deriveRcmGst().override
          ? (Number(deriveRcmGst().cgst) || 0)
          : (Number.parseFloat(formData.cgst_percentage) || 0),
        igst_percentage: gstExempt
          ? 0
          : deriveRcmGst().override
          ? (Number(deriveRcmGst().igst) || 0)
          : (Number.parseFloat(formData.igst_percentage) || 0),
        gst_exempt: gstExempt,
        paid_amount: Number.parseFloat(formData.paid_amount) || 0,
        shipment_client_id: formData.mode_of_payment === "credit" ? (formData.shipment_client_id || "") : "",
        payment_mode: formData.payment_mode || null,
        updated_by: user?.user_id,
        booking_agent_user_id: (canEditBookingAgent || senderBranchChanged) ? bookingAgentUserId : "",
        receiver_agent_user_id: showDestinationAgentPicker ? destinationAgentUserId : "",
        shipment_media: shipmentMediaFiles,
        remove_media_ids: removedMediaIds,
      }

      if (!ewaybillFeatureOn && submittedEwayBills.length > 0) {
        try {
          await shipmentService.addEwayBills(shipmentId, submittedEwayBills)
          Alert.success(`${submittedEwayBills.length} new E-way bill(s) added successfully`)
        } catch (ewayError) {
          console.error("Failed to add e-way bills:", ewayError)
          Alert.error("Shipment updated but failed to add E-way bills")
        }
      }
      await shipmentService.updateShipment(shipmentId, submitData)
      Alert.success("Shipment updated successfully")
      navigate("/shipments")
    } catch (error) {
      console.error("Error updating shipment:", error)
      Alert.error(error.response?.data?.error || "Failed to update shipment")
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <DashboardLayout fullWidth fillViewport>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading shipment..." />
        </div>
      </DashboardLayout>
    )
  }

  const isConsignorSuggestionsOpen =
    !isMobileLayout &&
    ((useShipmentClient && showClientDropdown && filteredClients.length > 0) ||
      (showConsignorSuggestions && (consignorSuggestions.length > 0 || consignorSuggestionsLoading)))

  const isConsigneeSuggestionsOpen =
    !isMobileLayout &&
    showConsigneeSuggestions &&
    (consigneeSuggestions.length > 0 || consigneeSuggestionsLoading)

  const renderPaymentStatusControl = ({ asCard = false } = {}) => {
    if (agentRequestPartyOnly) {
      const modeLabel = String(formData.mode_of_payment || "").replace(/_/g, " ").toUpperCase() || "—"
      return (
        <div
          className={
            asCard
              ? "bg-gray-50 border border-gray-200 rounded-xl px-2.5 py-1.5 sm:px-3 sm:py-2 h-full"
              : "flex items-center gap-2 shrink-0 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-xl border border-gray-200 bg-gray-50"
          }
        >
          <span className="text-xs sm:text-sm font-semibold text-gray-600 whitespace-nowrap">Payment Status</span>
          <span className="text-xs sm:text-sm font-bold text-gray-800">{modeLabel}</span>
          <span className="text-[10px] text-gray-500">(locked)</span>
        </div>
      )
    }
    return (
    <div
      className={
        asCard
          ? "bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl px-2.5 py-1.5 sm:px-3 sm:py-2 h-full"
          : "flex items-center gap-2 shrink-0 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50"
      }
    >
      <div className={asCard ? "flex items-center gap-2 min-w-0" : "contents"}>
        {asCard && <div className="w-2 h-2 bg-indigo-500 rounded-full shrink-0"></div>}
        <span className="text-xs sm:text-sm font-semibold text-gray-700 whitespace-nowrap">
          Payment Status
        </span>
      </div>
      <div className={`${asCard ? "mt-1.5 " : ""}grid gap-1 ${editPaymentStatusOptions.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
        {editPaymentStatusOptions.map((option) => {
          const selected = formData.mode_of_payment === option.value
          const creditNeedsClient = option.value === "credit" && !hasCreditClientSelected
          const locked = !canChangePaymentStatus || creditNeedsClient
          return (
            <button
              key={option.value}
              type="button"
              disabled={locked}
              title={
                creditNeedsClient
                  ? "Select a credit client first (Select from Credit Clients)"
                  : undefined
              }
              onClick={() => {
                if (!canChangePaymentStatus) return
                if (option.value === "credit" && !hasCreditClientSelected) {
                  Alert.error('Select a credit client first using "Select from Credit Clients".')
                  return
                }
                handleChange({ target: { name: "mode_of_payment", value: option.value } })
              }}
              className={`min-w-0 px-2 py-1.5 rounded-lg border text-[11px] sm:text-xs font-semibold leading-tight transition ${
                selected
                  ? "bg-blue-600 border-blue-600 text-white shadow-sm"
                  : "bg-white border-blue-200 text-blue-800 hover:border-blue-400 hover:bg-white/80"
              } ${locked ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {option.label}
            </button>
          )
        })}
      </div>
    </div>
    )
  }

  return (
    <DashboardLayout fullWidth fillViewport>
      <form
        id="edit-shipment-form"
        noValidate={isMobileLayout}
        onSubmit={handleFormSubmit}
        className={`min-w-0 flex flex-col flex-1 min-h-0 md:[&_input]:py-1.5 md:[&_select]:py-1.5 md:[&_textarea]:py-1.5 ${isMobileLayout ? (mobileStep === 4 ? "overflow-y-auto pb-20" : "overflow-hidden pb-20") : "md:overflow-hidden"}`}
      >
        {isMobileLayout && (
          <div className="sticky top-0 z-30 bg-white shrink-0 mb-3 pb-2 border-b border-gray-100">
            <div className="flex items-center gap-1 mb-2">
              {MOBILE_STEPS.map((step, idx) => (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => handleMobileStepTabClick(idx)}
                  className={`flex-1 min-w-0 text-center py-1.5 px-0.5 rounded-lg text-[10px] font-semibold transition truncate ${
                    idx === mobileStep
                      ? "bg-orange-500 text-white shadow-sm"
                      : idx < mobileStep
                        ? "bg-orange-100 text-orange-700"
                        : "bg-gray-100 text-gray-400"
                  }`}
                >
                  {step.label}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2">
              <h1 className="flex items-center gap-1.5 text-sm font-bold text-gray-700 truncate">
                <MapPin className="w-4 h-4 text-buttonhoverTxt shrink-0" />
                <span className="truncate">{MOBILE_STEPS[mobileStep].label}</span>
              </h1>
              <Button variant="secondary" type="button" size="sm" onClick={() => navigate("/shipments")}>
                Cancel
              </Button>
            </div>
            <div className="mt-2 overflow-x-auto">
              {renderPaymentStatusControl()}
            </div>
          </div>
        )}

        <div className="hidden md:block sticky top-0 z-30 mb-2 space-y-2 shrink-0 pb-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <h1 className="flex items-center gap-2 text-base sm:text-xl font-bold text-gray-700 truncate">
                <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-buttonhoverTxt shrink-0" />
                <span className="truncate">{isAgentRole ? "Request shipment update" : "Edit Shipment"}</span>
              </h1>
              {isAgentRole && (
                <p className="text-xs sm:text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5 mt-1">
                  Your changes are sent to admin or accountant for approval. The shipment updates only after they approve.
                  {agentRequestPartyOnly
                    ? " While Dispatched or Received, only consignor and consignee contact details can be changed."
                    : null}
                </p>
              )}
            </div>
            <div className="flex flex-row flex-wrap items-center gap-2 sm:gap-3 shrink-0">
              <Button variant="secondary" type="button" onClick={() => navigate("/shipments")}>
                Cancel
              </Button>
              <Button variant="secondary" icon={ArrowLeft} type="button" onClick={() => navigate("/shipments")} className="hidden sm:inline-flex">
                Back to Dashboard
              </Button>
              <Button id="edit-shipment-submit" variant="primary" type="button" onClick={handleFormSubmit} loading={saving} disabled={saving} className="hidden md:inline-flex">
                {saving ? "Sending..." : (isAgentRole ? "Send update request" : "Update Shipment")}
              </Button>
            </div>
          </div>
        </div>

        <div className={isMobileLayout ? "contents" : "md:flex-1 md:min-h-0 md:overflow-y-auto md:overflow-x-hidden md:min-w-0 md:pb-24"}>

        <div className={`${mobileStepPanelClass(0)} md:!block md:shrink-0 md:mb-2 md:space-y-2 max-md:gap-3`}>
          <div className="max-md:order-2">
          <div className={`grid grid-cols-1 gap-3 ${showAutoMlSection ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
            <div className="hidden md:block min-w-0">
              {renderPaymentStatusControl({ asCard: true })}
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl px-2.5 py-1.5 sm:px-3 sm:py-2">
              <div className="flex items-center justify-between gap-2 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse shrink-0"></div>
                  <span className="text-xs sm:text-sm font-semibold text-gray-700 whitespace-nowrap">GC Number</span>
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                  shipmentIdType === "manual"
                    ? "bg-orange-100 text-orange-700 border border-orange-300"
                    : "bg-green-100 text-green-700 border border-green-300"
                }`}>
                  {shipmentIdType === "manual" ? "Manual ID" : "Auto ID"}
                </span>
              </div>
              <div className="mt-1.5 flex items-center gap-2 min-w-0">
                <div className="flex items-center gap-2 bg-white px-2.5 py-1 rounded-lg border border-green-200 min-w-0 w-full">
                  <span className="text-xs sm:text-sm font-bold text-blue-700 truncate">{shipmentId}</span>
                  {consignmentNo && (
                    <span className="text-xs text-gray-500 shrink-0">CN: {consignmentNo}</span>
                  )}
                </div>
              </div>
            </div>
            {showAutoMlSection && (
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl px-2.5 py-1.5 sm:px-3 sm:py-2">
                <div className="flex items-center justify-between gap-2 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-2 h-2 rounded-full shrink-0 bg-gray-400"></div>
                    <span className="text-xs sm:text-sm font-semibold text-gray-700 whitespace-nowrap">Auto ML Creation</span>
                  </div>
                  <span className="flex items-center gap-2 select-none shrink-0">
                    <span className="relative w-10 h-5 rounded-full bg-gray-300">
                      <span className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow" />
                    </span>
                    <span className="text-xs text-gray-600 font-medium whitespace-nowrap">Disabled</span>
                  </span>
                </div>
                <div className="mt-1.5 flex items-center bg-white px-2.5 py-1 rounded-lg border border-gray-200">
                  <span className="text-xs text-gray-500 italic truncate">ML sheet will not be created automatically</span>
                </div>
              </div>
            )}
          </div>
          </div>

          <div className="bg-white rounded-xl p-3 sm:p-4 mb-2 min-w-0 shrink-0 max-md:order-1">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2 pb-2 border-b-2 border-gray-200">
              <h2 className="text-sm sm:text-base font-semibold text-blue-900">Destination & Documents</h2>
              <div className="flex flex-row gap-2 shrink-0 flex-wrap">
                {!ewaybillFeatureOn && (
                  <Button variant="secondary" type="button" tabIndex={-1} onClick={() => setShowEwayBillModal(true)}>
                    Add E-way Unique ID
                  </Button>
                )}
                <Button
                  variant={(existingEwayBills.length + submittedEwayBills.length) > 0 ? "primary" : "secondary"}
                  type="button"
                  tabIndex={-1}
                  onClick={handleViewEwayBills}
                >
                  View Eway Bill {(existingEwayBills.length + submittedEwayBills.length) > 0 && `(${existingEwayBills.length + submittedEwayBills.length})`}
                </Button>
              </div>
            </div>

            <div className={`flex flex-col gap-4 w-full ${showConsignerDropdown || showConsigneeDropdown ? "relative z-[80]" : ""}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 min-w-0 items-start">
              <div className="flex flex-col min-w-0 relative" ref={consignerRef}>
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  From Destination <span className="text-red-500">*</span>
                </label>
                {canAssignBookingAgent ? (
                  <div className="relative">
                    <div
                      onClick={() => { setShowConsignerDropdown(true); setConsignerHighlightIndex(-1); }}
                      className={`px-3 sm:px-4 py-2 min-h-[42px] text-sm sm:text-base border rounded-xl cursor-pointer select-none overflow-hidden ${
                        formData.consigner_location ? "border-gray-300 bg-white hover:border-orange-400" : "border-red-300 bg-white"
                      }`}
                    >
                      {formData.consigner_location ? (
                        <span className="block text-gray-800 leading-snug break-words">{formData.consigner_location}</span>
                      ) : (
                        <span className="text-gray-400">Select from branch</span>
                      )}
                    </div>
                    {showConsignerDropdown && (
                      <div className="absolute z-[80] w-full mt-1 top-full bg-white border border-gray-300 rounded-xl shadow-lg">
                        <div className="p-2 border-b border-gray-200">
                          <input
                            type="text"
                            autoFocus
                            value={consignerLocationSearch}
                            onChange={(e) => { setConsignerLocationSearch(e.target.value); setConsignerHighlightIndex(-1); }}
                            onKeyDown={(e) =>
                              handleLocationDropdownKeyDown(e, {
                                isOpen: showConsignerDropdown,
                                options: filteredConsignerLocations,
                                highlightIndex: consignerHighlightIndex,
                                setHighlightIndex: setConsignerHighlightIndex,
                                onClose: () => { setShowConsignerDropdown(false); setConsignerLocationSearch(""); },
                                onSelect: selectFromBranch,
                              })
                            }
                            placeholder="Type to search..."
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="max-h-52 overflow-y-auto">
                          {filteredConsignerLocations.length === 0 ? (
                            <div className="px-4 py-3 text-sm text-gray-400 text-center">No locations found</div>
                          ) : filteredConsignerLocations.map((loc, idx) => (
                            <div
                              key={loc.hub_id || loc.id}
                              onMouseEnter={() => setConsignerHighlightIndex(idx)}
                              onClick={() => selectFromBranch(loc)}
                              className={`px-3 sm:px-4 py-2 cursor-pointer text-sm sm:text-base border-b border-gray-100 last:border-b-0 ${
                                idx === consignerHighlightIndex ? "bg-orange-50" : "hover:bg-orange-50"
                              }`}
                            >
                              {formatHubBranchLabel(loc)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="px-3 sm:px-4 py-2 min-h-[42px] text-sm sm:text-base border border-gray-300 rounded-xl bg-gray-100 text-gray-700 cursor-not-allowed select-none overflow-hidden">
                    {formData.consigner_location ? (
                      <span className="block text-gray-800 leading-snug break-words">{formData.consigner_location}</span>
                    ) : (
                      <span className="text-gray-400">From branch</span>
                    )}
                  </div>
                )}
                {canAssignBookingAgent && formData.sender_location_hub_id && (
                  <div className="mt-2 relative" ref={bookingAgentDropdownRef}>
                    <label className="block text-xs font-medium text-secondary mb-1">
                      Agent at this branch
                      {(senderBranchChanged || (canEditBookingAgent && bookingAgents.length > 1)) && <span className="text-red-500"> *</span>}
                    </label>
                    {bookingAgentsLoading ? (
                      <p className="text-xs text-gray-500 italic">Loading agents...</p>
                    ) : bookingAgents.length === 0 ? (
                      <p className="text-xs text-gray-500 italic">
                        {senderBranchChanged
                          ? "No active agent at this branch. Choose another from branch, or assign an agent there before saving."
                          : "No agents at this branch. Ownership stays as booked."}
                      </p>
                    ) : lockAgentPicker ? (
                      <>
                        <div className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-gray-50 text-gray-800 cursor-not-allowed">
                          {(() => {
                            const a = bookingAgents.find((ag) => ag.user_id === bookingAgentUserId) || bookingAgents[0]
                            return a
                              ? `${a.name}${a.city ? ` — ${a.city}` : ""} (${a.user_id})`
                              : "—"
                          })()}
                        </div>
                        {!canEditBookingAgent && (
                          <p className="mt-1 text-[11px] text-gray-400">
                            Booking agent cannot be changed after the shipment is dispatched.
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <div
                          onClick={() => setShowBookingAgentDropdown((prev) => !prev)}
                          className={`px-3 py-2 h-[42px] text-sm border rounded-xl cursor-pointer select-none flex items-center justify-between bg-white hover:border-orange-400 ${
                            bookingAgentUserId ? "border-gray-300" : "border-red-300"
                          }`}
                        >
                          <span className={bookingAgentUserId ? "text-gray-800 truncate" : "text-gray-400"}>
                            {bookingAgentUserId
                              ? (() => {
                                  const a = bookingAgents.find((ag) => ag.user_id === bookingAgentUserId)
                                  return a
                                    ? `${a.name}${a.city ? ` — ${a.city}` : ""} (${a.user_id})`
                                    : "Select agent at this branch"
                                })()
                              : "Select agent at this branch"}
                          </span>
                          <span className="text-gray-400 ml-2 shrink-0">▾</span>
                        </div>
                        {showBookingAgentDropdown && (
                          <div className="absolute z-[90] w-full mt-1 top-full bg-white border border-gray-300 rounded-xl shadow-xl">
                            <div className="p-2 border-b border-gray-200">
                              <input
                                type="text"
                                autoFocus
                                value={bookingAgentSearch}
                                onChange={(e) => setBookingAgentSearch(e.target.value)}
                                placeholder="Search agent..."
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                            <div className="max-h-48 overflow-y-auto">
                              {bookingAgents
                                .filter((agent) => {
                                  if (!bookingAgentSearch.trim()) return true
                                  const q = bookingAgentSearch.toLowerCase()
                                  return (
                                    String(agent.name || "").toLowerCase().includes(q) ||
                                    String(agent.user_id || "").toLowerCase().includes(q) ||
                                    String(agent.city || "").toLowerCase().includes(q)
                                  )
                                })
                                .map((agent) => (
                                  <div
                                    key={agent.user_id}
                                    onClick={() => {
                                      setBookingAgentUserId(agent.user_id)
                                      setShowBookingAgentDropdown(false)
                                      setBookingAgentSearch("")
                                    }}
                                    className={`px-3 py-2 cursor-pointer text-sm border-b border-gray-100 last:border-b-0 hover:bg-orange-50 ${
                                      agent.user_id === bookingAgentUserId ? "bg-emerald-100 font-semibold" : ""
                                    }`}
                                  >
                                    {agent.name}
                                    {agent.city ? ` — ${agent.city}` : ""}
                                    <span className="text-gray-400 ml-1">({agent.user_id})</span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {senderBranchChanged && bookingAgents.length > 0 && (
                      <p className="mt-1 text-[11px] text-gray-500">
                        Required for this from-branch change. The selected agent becomes the booking owner.
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex flex-col min-w-0 relative self-start" ref={consigneeRef}>
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  To Destination <span className="text-red-500">*</span>
                </label>
                {isAgentRole && !agentCanEditToDestination ? (
                  <div className="px-3 sm:px-4 py-2 min-h-[42px] text-sm sm:text-base border border-gray-300 rounded-xl bg-gray-100 text-gray-700 cursor-not-allowed select-none overflow-hidden">
                    {formData.consignee_location ? (
                      <span className="block text-gray-800 leading-snug break-words">{formData.consignee_location}</span>
                    ) : (
                      <span className="text-gray-400">To branch</span>
                    )}
                  </div>
                ) : (
                <>
                <div
                  onClick={() => { setShowConsigneeDropdown(true); setConsigneeHighlightIndex(-1); }}
                  className={`px-3 sm:px-4 py-2 min-h-[42px] text-sm sm:text-base border rounded-xl cursor-pointer select-none overflow-hidden ${
                    formData.consignee_location ? "border-gray-300 bg-white hover:border-orange-400" : "border-red-300 bg-white hover:border-red-400"
                  }`}
                >
                  {formData.consignee_location ? (
                    <span className="block text-gray-800 leading-snug break-words">{formData.consignee_location}</span>
                  ) : (
                    <span className="text-gray-400">Select Location</span>
                  )}
                </div>
                {showConsigneeDropdown && (
                  <div className="absolute z-50 w-full mt-1 top-full bg-white border border-gray-300 rounded-xl shadow-lg">
                    <div className="p-2 border-b border-gray-200">
                      <input
                        type="text"
                        autoFocus
                        value={consigneeLocationSearch}
                        onChange={(e) => { setConsigneeLocationSearch(e.target.value); setConsigneeHighlightIndex(-1); }}
                        onKeyDown={(e) =>
                          handleLocationDropdownKeyDown(e, {
                            isOpen: showConsigneeDropdown,
                            options: filteredConsigneeLocations,
                            highlightIndex: consigneeHighlightIndex,
                            setHighlightIndex: setConsigneeHighlightIndex,
                            onClose: () => { setShowConsigneeDropdown(false); setConsigneeLocationSearch(""); },
                            onSelect: (loc) => {
                              const label = formatHubBranchLabel(loc)
                              if ((loc?.id ?? "") === formData.sender_location_hub_id && formData.sender_location_hub_id !== "") {
                                Alert.error("Sender and Receiver locations cannot be the same")
                                return
                              }
                              const hubRate = rateFromHub(loc)
                              setFormData({
                                ...formData,
                                consignee_location: label,
                                receiver_location_hub_id: loc?.id ?? "",
                                ...(hubRate !== null ? { rate_per_kg: hubRate } : {}),
                              })
                              setConsigneeLocationSearch("")
                              setShowConsigneeDropdown(false)
                              setConsigneeHighlightIndex(-1)
                            },
                          })
                        }
                        placeholder="Type to search..."
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="max-h-52 overflow-y-auto">
                      {filteredConsigneeLocations.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-400 text-center">No locations found</div>
                      ) : filteredConsigneeLocations.map((loc, idx) => (
                        <div
                          key={loc.hub_id}
                          onMouseEnter={() => setConsigneeHighlightIndex(idx)}
                          onClick={() => {
                            const label = formatHubBranchLabel(loc)
                            if ((loc?.id ?? "") === formData.sender_location_hub_id && formData.sender_location_hub_id !== "") {
                              Alert.error("Sender and Receiver locations cannot be the same")
                              return
                            }
                            const hubRate = rateFromHub(loc)
                            setFormData({
                              ...formData,
                              consignee_location: label,
                              receiver_location_hub_id: loc?.id ?? "",
                              ...(hubRate !== null ? { rate_per_kg: hubRate } : {}),
                            })
                            setConsigneeLocationSearch("")
                            setShowConsigneeDropdown(false)
                            setConsigneeHighlightIndex(-1)
                          }}
                          className={`px-3 sm:px-4 py-2 cursor-pointer text-sm sm:text-base border-b border-gray-100 last:border-b-0 ${
                            idx === consigneeHighlightIndex ? "bg-orange-50" : "hover:bg-orange-50"
                          }`}
                        >
                          {formatHubBranchLabel(loc)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                </>
                )}
                {showDestinationAgentPicker && (
                  <div className="mt-2 relative" ref={destinationAgentDropdownRef}>
                    <label className="block text-xs font-medium text-secondary mb-1">
                      Agent at this destination <span className="text-red-500">*</span>
                    </label>
                    {destinationAgentsLoading ? (
                      <p className="text-xs text-gray-500 italic">Loading agents...</p>
                    ) : destinationAgents.length === 0 ? (
                      <p className="text-xs text-gray-500 italic">
                        No active agent at this destination. Assign an agent there before saving.
                      </p>
                    ) : destinationAgents.length === 1 ? (
                      <div className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-gray-50 text-gray-800">
                        {destinationAgents[0].name}
                        {destinationAgents[0].city ? ` — ${destinationAgents[0].city}` : ""}
                        <span className="text-gray-400 ml-1">({destinationAgents[0].user_id})</span>
                      </div>
                    ) : (
                      <>
                        <div
                          onClick={() => setShowDestinationAgentDropdown((prev) => !prev)}
                          className={`px-3 py-2 h-[42px] text-sm border rounded-xl cursor-pointer select-none flex items-center justify-between bg-white hover:border-orange-400 ${
                            destinationAgentUserId ? "border-gray-300" : "border-red-300"
                          }`}
                        >
                          <span className={destinationAgentUserId ? "text-gray-800 truncate" : "text-gray-400"}>
                            {destinationAgentUserId
                              ? (() => {
                                  const a = destinationAgents.find((ag) => ag.user_id === destinationAgentUserId)
                                  return a
                                    ? `${a.name}${a.city ? ` — ${a.city}` : ""} (${a.user_id})`
                                    : "Select agent at this destination"
                                })()
                              : "Select agent at this destination"}
                          </span>
                          <span className="text-gray-400 ml-2 shrink-0">▾</span>
                        </div>
                        {showDestinationAgentDropdown && (
                          <div className="absolute z-[90] w-full mt-1 top-full bg-white border border-gray-300 rounded-xl shadow-xl">
                            <div className="p-2 border-b border-gray-200">
                              <input
                                type="text"
                                autoFocus
                                value={destinationAgentSearch}
                                onChange={(e) => setDestinationAgentSearch(e.target.value)}
                                placeholder="Search agent..."
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                            <div className="max-h-48 overflow-y-auto">
                              {destinationAgents
                                .filter((agent) => {
                                  if (!destinationAgentSearch.trim()) return true
                                  const q = destinationAgentSearch.toLowerCase()
                                  return (
                                    String(agent.name || "").toLowerCase().includes(q) ||
                                    String(agent.user_id || "").toLowerCase().includes(q) ||
                                    String(agent.city || "").toLowerCase().includes(q)
                                  )
                                })
                                .map((agent) => (
                                  <div
                                    key={agent.user_id}
                                    onClick={() => {
                                      setDestinationAgentUserId(agent.user_id)
                                      setShowDestinationAgentDropdown(false)
                                      setDestinationAgentSearch("")
                                    }}
                                    className={`px-3 py-2 cursor-pointer text-sm border-b border-gray-100 last:border-b-0 hover:bg-orange-50 ${
                                      agent.user_id === destinationAgentUserId ? "bg-emerald-100 font-semibold" : ""
                                    }`}
                                  >
                                    {agent.name}
                                    {agent.city ? ` — ${agent.city}` : ""}
                                    <span className="text-gray-400 ml-1">({agent.user_id})</span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {destinationAgents.length > 0 && (
                      <p className="mt-1 text-[11px] text-gray-500">
                        This agent receives the parcel and delivers it. The previous destination agent will no longer see it.
                      </p>
                    )}
                  </div>
                )}
              </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 min-w-0">
              <div className="flex flex-col min-w-0 items-start">
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Booking Date</label>
                <input type="date" name="booking_date" value={formData.booking_date} onChange={handleChange} onKeyDown={handleEnterAsTab} max={getLocalDateString()} disabled={isAgentRole} readOnly={isAgentRole} className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 disabled:bg-gray-100 disabled:cursor-not-allowed" />
              </div>
              <div className="flex flex-col min-w-0">
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Invoice No</label>
                <input
                  type="text"
                  name="invoice_number"
                  placeholder="Optional"
                  value={formData.invoice_number}
                  onChange={handleChange}
                  onKeyDown={handleEnterAsTab}
                  className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200"
                />
              </div>
              <div className="flex flex-col min-w-0">
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Invoice Value</label>
                <input
                  type="text"
                  inputMode="numeric"
                  name="invoice_value"
                  placeholder="Optional"
                  value={formData.invoice_value}
                  onChange={handleChange}
                  onKeyDown={handleEnterAsTab}
                  className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200"
                />
                {(Number.parseInt(formData.invoice_value, 10) || 0) > EWAY_BILL_MANDATORY_VALUE ? (
                  <span className="text-[10px] text-red-600 mt-1">E-way bill and GST number are required</span>
                ) : null}
              </div>
              </div>
            </div>

            {(loadingEwayBills || existingEwayBills.length > 0 || submittedEwayBills.length > 0) && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                {loadingEwayBills ? (
                  <p className="text-xs text-gray-500">Loading E-way bills...</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {existingEwayBills.map((bill) => (
                      <div key={bill.id} className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-2 py-1">
                        <span className="text-xs font-mono text-gray-800">{bill.eway_bill_number}</span>
                        {bill.status === 'cancelled' && (
                          <span className="text-[10px] text-red-600 font-medium">Cancelled</span>
                        )}
                        {ewaybillFeatureOn && bill.status !== 'cancelled' ? (
                          <>
                            <button
                              type="button"
                              tabIndex={-1}
                              onClick={() => {
                                setSelectedEwayBill(bill)
                                setUpdateVehicleForm({
                                  vehicleNo: bill.vehicle_no || formData.vehicle_number || '',
                                  reasonRem: 'Vehicle Update',
                                })
                                setShowUpdateVehicleModal(true)
                              }}
                              className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                            >
                              Vehicle
                            </button>
                            <button
                              type="button"
                              tabIndex={-1}
                              onClick={() => handleCancelEwayBill(bill)}
                              className="text-red-600 hover:text-red-800 text-xs font-medium"
                            >
                              Cancel
                            </button>
                          </>
                        ) : !ewaybillFeatureOn ? (
                          <button type="button" tabIndex={-1} onClick={() => handleDeleteEwayBill(bill.id, bill.eway_bill_number)} className="text-red-600 hover:text-red-800 text-xs font-medium">Delete</button>
                        ) : null}
                      </div>
                    ))}
                    {!ewaybillFeatureOn && submittedEwayBills.map((bill, idx) => (
                      <div key={`new-${idx}`} className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1">
                        <span className="text-xs font-mono text-gray-800">{bill}</span>
                        <button type="button" tabIndex={-1} onClick={() => { setSubmittedEwayBills(submittedEwayBills.filter((_, i) => i !== idx)); Alert.success("E-way bill removed from queue"); }} className="text-red-600 hover:text-red-800 text-xs font-medium">Remove</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className={`space-y-3 md:space-y-3 min-w-0 flex-1 min-h-0 ${isMobileLayout ? (mobileStep === 4 ? "flex flex-col overflow-visible min-h-0 flex-1" : "flex flex-col overflow-hidden min-h-0 flex-1") : "md:overflow-visible"} ${isMobileLayout && mobileStep === 0 ? "hidden" : ""}`}>
          {/* Consignor (left) | Consignee (right) — stacked on mobile wizard */}
          <div className={`${isMobileLayout ? "contents" : `grid grid-cols-1 md:grid-cols-2 gap-3 relative px-4 ${isConsigneeSuggestionsOpen ? "z-[60]" : ""}`}`}>
              <div className={`rounded-xl pb-2 sm:pb-3 bg-white p-0 md:p-0 md:pt-0 border ${mobileStepPanelClass(1)} md:!block md:overflow-visible ${isConsignorSuggestionsOpen ? "relative z-50" : ""}`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2 sm:mb-2 pt-2 pb-2 border-b-2 border-gray-200 px-4 bg-blue-50">
                  <h2 className="text-base sm:text-md font-semibold text-blue-900 mt-0 pb-0">Consignor Information</h2>
                  <div className="flex items-center gap-3 shrink-0">
                    {creditAllowed && (
                    <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 cursor-pointer">
                      <input type="checkbox" id="useShipmentClient" checked={useShipmentClient} onChange={(e) => handleUseClientChange(e.target.checked)} className="w-4 h-4 text-orange-600 focus:ring-blue-400 border-gray-300 rounded" />
                      Select from Credit Clients
                    </label>
                    )}
                  </div>
                </div>
                <div className="space-y-0 p-4 py-2 pb-4">
                  <div className={`grid grid-cols-1 gap-3 sm:gap-3 mb-3 ${isMobileLayout ? "" : "sm:grid-cols-2"}`}>
                    <div className="flex flex-col relative">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Contact Number <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="consigner_contact"
                        placeholder="10 digit mobile number"
                        value={formData.consigner_contact}
                        onChange={(e) => {
                          handleChange(e)
                          if (!useShipmentClient) {
                            setConsignorSuggestionHighlightIndex(-1)
                            fetchConsignorSuggestions(e.target.value)
                          }
                        }}
                        onFocus={() => {
                          if (!useShipmentClient && String(formData.consigner_contact || "").replace(/\D/g, "").length >= 10) {
                            setShowConsignorSuggestions(true)
                            setConsignorSuggestionHighlightIndex(-1)
                          }
                        }}
                        onBlur={() => setTimeout(() => { setShowConsignorSuggestions(false); setConsignorSuggestionHighlightIndex(-1) }, 200)}
                        onKeyDown={(e) =>
                          handleSuggestionDropdownKeyDown(e, {
                            isOpen: showConsignorSuggestions && !consignorSuggestionsLoading && consignorSuggestions.length > 0,
                            options: consignorSuggestions,
                            highlightIndex: consignorSuggestionHighlightIndex,
                            setHighlightIndex: setConsignorSuggestionHighlightIndex,
                            onSelect: handleConsignorSuggestionSelect,
                            onClose: () => { setShowConsignorSuggestions(false); setConsignorSuggestionHighlightIndex(-1) },
                            onEnterFallback: handleEnterAsTab,
                          })
                        }
                        required
                        autoComplete="off"
                        disabled={useShipmentClient}
                        maxLength={10}
                        className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border ${errors.consigner_contact ? "border-red-400" : "border-gray-300"} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 disabled:bg-gray-100`}
                      />
                      <ValidationMessage error={errors.consigner_contact} />
                      {showConsignorSuggestions && (consignorSuggestions.length > 0 || consignorSuggestionsLoading) && !useShipmentClient && (
                        <div className={getSuggestionListClass(isMobileLayout)}>
                          {consignorSuggestionsLoading && (
                            <div className="px-4 py-3 text-sm text-gray-500 text-center">Loading...</div>
                          )}
                          {!consignorSuggestionsLoading && consignorSuggestions.length === 0 && (
                            <div className="px-4 py-3 text-sm text-gray-500 text-center">No suggestions found</div>
                          )}
                          {Array.isArray(consignorSuggestions) && consignorSuggestions.map((suggestion, index) => (
                            <div
                              key={index}
                              ref={(node) => { if (index === consignorSuggestionHighlightIndex) scrollHighlightedSuggestionIntoView(node) }}
                              onMouseEnter={() => setConsignorSuggestionHighlightIndex(index)}
                              onClick={() => handleConsignorSuggestionSelect(suggestion)}
                              className={getSuggestionItemClass(index === consignorSuggestionHighlightIndex)}
                            >
                              <div className="font-medium text-gray-800">{suggestion.name}</div>
                              <div className="text-xs text-gray-500">{suggestion.contact}</div>
                              {suggestion.address && <div className="text-xs text-gray-400 truncate">{suggestion.address}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className={`flex flex-col relative ${isMobileLayout ? "" : "z-30"}`}>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Consignor Name <span className="text-red-500">*</span></label>
                      {useShipmentClient ? (
                        <>
                          <input
                            type="text"
                            value={selectedClient ? `${selectedClient.full_name} (${selectedClient.company_name})` : clientSearch}
                            onChange={(e) => { setClientSearch(e.target.value); setSelectedClient(null); setShowClientDropdown(true); setClientHighlightIndex(-1); }}
                            onFocus={() => { setShowClientDropdown(true); setClientHighlightIndex(-1); }}
                            onBlur={() => setTimeout(() => { setShowClientDropdown(false); setClientHighlightIndex(-1) }, 200)}
                            onKeyDown={(e) =>
                              handleSuggestionDropdownKeyDown(e, {
                                isOpen: showClientDropdown,
                                options: filteredClients,
                                highlightIndex: clientHighlightIndex,
                                setHighlightIndex: setClientHighlightIndex,
                                onSelect: handleClientSelect,
                                onClose: () => { setShowClientDropdown(false); setClientHighlightIndex(-1) },
                                onEnterFallback: handleEnterAsTab,
                              })
                            }
                            className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200"
                            required
                          />
                          {showClientDropdown && filteredClients.length > 0 && (
                            <div className={getSuggestionListClass(isMobileLayout)}>
                              {filteredClients.map((client, index) => (
                                <div
                                  key={index}
                                  ref={(node) => { if (index === clientHighlightIndex) scrollHighlightedSuggestionIntoView(node) }}
                                  onMouseEnter={() => setClientHighlightIndex(index)}
                                  onClick={() => handleClientSelect(client)}
                                  className={getSuggestionItemClass(index === clientHighlightIndex)}
                                >
                                  <div className="font-medium text-gray-800">{client.full_name}</div>
                                  <div className="text-xs text-gray-500">{client.contact}</div>
                                  {client.address && <div className="text-xs text-gray-400 truncate">{client.address}</div>}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <input
                            type="text"
                            name="consigner_name"
                            placeholder="Consignor name"
                            value={formData.consigner_name}
                            onChange={handleChange}
                            onKeyDown={handleEnterAsTab}
                            required
                            autoComplete="off"
                            className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border ${errors.consigner_name ? "border-red-400" : "border-gray-300"} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200`}
                          />
                          <ValidationMessage error={errors.consigner_name} />
                        </>
                      )}
                    </div>
                  </div>
                  <div className={`grid grid-cols-1 gap-3 sm:gap-3 mb-3 ${isMobileLayout ? "" : "sm:grid-cols-3"}`}>
                    <GstVerificationField
                      name="consigner_gst"
                      value={formData.consigner_gst}
                      onChange={handleChange}
                      onKeyDown={handleEnterAsTab}
                      error={errors.consigner_gst}
                      enabled={gstVerificationOn}
                      required={(Number.parseInt(formData.invoice_value, 10) || 0) > EWAY_BILL_MANDATORY_VALUE}
                      placeholder={(Number.parseInt(formData.invoice_value, 10) || 0) > EWAY_BILL_MANDATORY_VALUE ? "GST Number" : "GST Number (Optional)"}
                      disabled={useShipmentClient && selectedClient}
                      onLookup={onSenderGstLookup}
                    />
                    <div className="flex flex-col min-w-0">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Email</label>
                      <input type="email" name="consigner_email" placeholder="Email address" value={formData.consigner_email} onChange={handleChange} onKeyDown={handleEnterAsTab} disabled={useShipmentClient && selectedClient} className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border ${errors.consigner_email ? "border-red-400" : "border-gray-300"} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 disabled:bg-gray-100`} />
                      <ValidationMessage error={errors.consigner_email} />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Address</label>
                      <input type="text" name="consigner_address" placeholder="Full Address" value={formData.consigner_address} onChange={handleChange} onKeyDown={handleEnterAsTab} disabled={useShipmentClient && selectedClient} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 disabled:bg-gray-100" />
                    </div>
                  </div>
                </div>
              </div>

              <div className={`rounded-xl pb-2 sm:pb-3 bg-white p-0 md:p-0 md:pt-0 border ${mobileStepPanelClass(2)} md:!block md:overflow-visible ${isConsigneeSuggestionsOpen ? "relative z-[70]" : ""}`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2 sm:mb-2 pt-2 pb-2 border-b-2 border-gray-200 px-4 bg-blue-50">
                  <h2 className="text-base sm:text-md font-semibold text-blue-900 mt-0 pb-0">Consignee Information</h2>
                  {creditAllowed && (
                    <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.credit_client_side === "receiver"}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            credit_client_side: e.target.checked ? "receiver" : (prev.shipment_client_id ? "sender" : ""),
                            mode_of_payment: prev.mode_of_payment,
                          }))
                        }}
                        className="w-4 h-4 text-orange-600 focus:ring-blue-400 border-gray-300 rounded"
                      />
                      Select from Credit Clients
                    </label>
                  )}
                </div>
                <div className="space-y-0 p-4 py-2 pb-4">
                  <div className={`grid grid-cols-1 gap-3 sm:gap-3 mb-3 ${isMobileLayout ? "" : "sm:grid-cols-2"}`}>
                    <div className={`flex flex-col relative ${isMobileLayout ? "" : "z-30"}`}>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Contact Number <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="consignee_contact"
                        placeholder="10 digit mobile number"
                        value={formData.consignee_contact}
                        onChange={(e) => {
                          handleChange(e)
                          setConsigneeSuggestionHighlightIndex(-1)
                          fetchConsigneeSuggestions(e.target.value)
                        }}
                        onFocus={() => {
                          if (String(formData.consignee_contact || "").replace(/\D/g, "").length >= 10) {
                            setShowConsigneeSuggestions(true)
                            setConsigneeSuggestionHighlightIndex(-1)
                          }
                        }}
                        onBlur={() => setTimeout(() => { setShowConsigneeSuggestions(false); setConsigneeSuggestionHighlightIndex(-1) }, 200)}
                        onKeyDown={(e) =>
                          handleSuggestionDropdownKeyDown(e, {
                            isOpen: showConsigneeSuggestions && !consigneeSuggestionsLoading && consigneeSuggestions.length > 0,
                            options: consigneeSuggestions,
                            highlightIndex: consigneeSuggestionHighlightIndex,
                            setHighlightIndex: setConsigneeSuggestionHighlightIndex,
                            onSelect: handleConsigneeSuggestionSelect,
                            onClose: () => { setShowConsigneeSuggestions(false); setConsigneeSuggestionHighlightIndex(-1) },
                            onEnterFallback: handleEnterAsTab,
                          })
                        }
                        required
                        autoComplete="off"
                        maxLength={10}
                        className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border ${errors.consignee_contact ? "border-red-400" : "border-gray-300"} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200`}
                      />
                      <ValidationMessage error={errors.consignee_contact} />
                      {showConsigneeSuggestions && (consigneeSuggestions.length > 0 || consigneeSuggestionsLoading) && (
                        <div className={getSuggestionListClass(isMobileLayout)}>
                          {consigneeSuggestionsLoading && (
                            <div className="px-4 py-3 text-sm text-gray-500 text-center">Loading...</div>
                          )}
                          {!consigneeSuggestionsLoading && consigneeSuggestions.length === 0 && (
                            <div className="px-4 py-3 text-sm text-gray-500 text-center">No suggestions found</div>
                          )}
                          {Array.isArray(consigneeSuggestions) && consigneeSuggestions.map((suggestion, index) => (
                            <div
                              key={index}
                              ref={(node) => { if (index === consigneeSuggestionHighlightIndex) scrollHighlightedSuggestionIntoView(node) }}
                              onMouseEnter={() => setConsigneeSuggestionHighlightIndex(index)}
                              onClick={() => handleConsigneeSuggestionSelect(suggestion)}
                              className={getSuggestionItemClass(index === consigneeSuggestionHighlightIndex)}
                            >
                              <div className="font-medium text-gray-800">{suggestion.name}</div>
                              <div className="text-xs text-gray-500">{suggestion.contact}</div>
                              {suggestion.address && <div className="text-xs text-gray-400 truncate">{suggestion.address}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className={`flex flex-col relative ${isMobileLayout ? "" : "z-30"}`}>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Consignee Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="consignee_name"
                        placeholder="Consignee name"
                        value={formData.consignee_name}
                        onChange={handleChange}
                        onKeyDown={handleEnterAsTab}
                        required
                        autoComplete="off"
                        className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border ${errors.consignee_name ? "border-red-400" : "border-gray-300"} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200`}
                      />
                      <ValidationMessage error={errors.consignee_name} />
                    </div>
                  </div>
                  <div className={`grid grid-cols-1 gap-3 sm:gap-3 mb-3 ${isMobileLayout ? "" : "sm:grid-cols-3"}`}>
                    <GstVerificationField
                      name="consignee_gst"
                      value={formData.consignee_gst}
                      onChange={handleChange}
                      onKeyDown={handleEnterAsTab}
                      error={errors.consignee_gst}
                      enabled={gstVerificationOn}
                      required={(Number.parseInt(formData.invoice_value, 10) || 0) > EWAY_BILL_MANDATORY_VALUE}
                      placeholder={(Number.parseInt(formData.invoice_value, 10) || 0) > EWAY_BILL_MANDATORY_VALUE ? "GST Number" : "GST Number (Optional)"}
                      onLookup={onReceiverGstLookup}
                    />
                    <div className="flex flex-col min-w-0">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Email</label>
                      <input type="email" name="consignee_email" placeholder="Email address" value={formData.consignee_email} onChange={handleChange} onKeyDown={handleEnterAsTab} className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border ${errors.consignee_email ? "border-red-400" : "border-gray-300"} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200`} />
                      <ValidationMessage error={errors.consignee_email} />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Address</label>
                      <input type="text" name="consignee_address" placeholder="Full Address" value={formData.consignee_address} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200" />
                    </div>
                  </div>
                </div>
              </div>
          </div>

          {/* Shipment Details (left) | Shipment Charges (right) — stacked on mobile wizard */}
          {!agentRequestPartyOnly && (
          <div className={`${isMobileLayout ? "contents" : "grid grid-cols-1 md:grid-cols-2 gap-3 md:min-h-0"}`}>
            <div className={`bg-white rounded-xl p-3 sm:p-4 mb-2 md:overflow-y-auto md:min-h-0 ${mobileStepPanelClass(3)} md:!block`}>
                <div className="flex items-center justify-between gap-2 mb-2 sm:mb-2 pb-1.5 border-b-2 border-gray-200">
                  <h2 className="text-base sm:text-lsm font-semibold text-blue-900 min-w-0">
                    Shipment Details
                  </h2>
                  <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowParcelImages(!showParcelImages)}
                      className="flex items-center justify-center p-1 rounded hover:bg-gray-100 transition"
                      aria-label="Toggle parcel images"
                    >
                      {showParcelImages ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                  </div>
                </div>
                <div className="space-y-0">
                  {isCargoMode ? (
                    <PackageDimensionSummary
                      parcels={parcels}
                      mode="edit"
                      onAdd={() => {
                        setAddPackageAppendMode(false)
                        setShowAddPackageModal(true)
                      }}
                      onEdit={() => setShowEditPackageModal(true)}
                      onView={() => setShowViewPackageModal(true)}
                      onAddMore={() => {
                        setAddPackageAppendMode(true)
                        setShowAddPackageModal(true)
                      }}
                    />
                  ) : (
                    <BusArticlePackagesForm
                      articles={parcels}
                      onChange={handleBusArticlesChange}
                      freightByWeight={busFreightByWeight}
                      title="Article Details"
                      badge="Bus"
                    />
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 mb-4">
                    <div className="rounded-xl border border-gray-200 bg-white p-3 sm:p-4 mt-4">
                      <h3 className="text-sm sm:text-base font-semibold text-blue-900 mb-2 sm:mb-3">
                        Photos &amp; Videos
                      </h3>
                      <ShipmentMediaUploader
                        files={shipmentMediaFiles}
                        onFilesChange={setShipmentMediaFiles}
                        existing={existingMedia}
                        removedIds={removedMediaIds}
                        onRemovedIdsChange={setRemovedMediaIds}
                        compact
                        dropzone={isMobileLayout}
                      />
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white p-3 sm:p-4 mt-4">
                      <label className="block text-sm sm:text-base font-semibold text-blue-900 mb-2 sm:mb-3">
                        Remarks / Parcel Details
                      </label>
                      <textarea
                        name="parcel_detail"
                        value={formData.parcel_detail}
                        onChange={handleChange}
                        onKeyDown={handleEnterAsTab}
                        rows="1"
                        placeholder="Notes, special instructions, parcel description..."
                        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 min-h-[50px]"
                      />
                    </div>
                  </div>

                  {showParcelImages && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 border p-4 mb-4">
                      {!isCargoMode && !busFreightByWeight && (
                        <div className="flex flex-col">
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Weight (kg)</label>
                          <input type="text" name="weight" value={formData.weight} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200" />
                        </div>
                      )}
                      {showBulkLoadOption && (
                        <div className="flex flex-col justify-center">
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Bulk Load</label>
                          <div className="flex items-center gap-4 h-[42px]">
                            <label className="flex items-center gap-1.5 text-sm text-gray-700">
                              <input type="radio" name="bulk_load" checked={!bulkLoad} onChange={() => setBulkLoad(false)} />
                              No
                            </label>
                            <label className="flex items-center gap-1.5 text-sm text-gray-700">
                              <input type="radio" name="bulk_load" checked={bulkLoad} onChange={() => setBulkLoad(true)} />
                              Yes
                            </label>
                          </div>
                        </div>
                      )}
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Shipment Value</label>
                        <input type="text" name="shipment_value" value={formData.shipment_value} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200" />
                      </div>
                    </div>
                  )}
                </div>
            </div>

            <div className={`bg-white rounded-xl p-3 sm:p-4 mb-2 md:overflow-y-auto md:min-h-0 overflow-visible ${isMobileLayout ? (mobileStep === 4 ? "block w-full min-w-0 overflow-visible pb-28" : "hidden") : ""} md:!block`}>
                <div className="flex items-center justify-between mb-2 sm:mb-2 pb-1.5 border-b-2 border-gray-200">
                  <h2 className="text-base sm:text-lsm font-semibold text-blue-900">Shipment Charges</h2>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    {gstExempt && (
                      <span className="px-2 py-1 text-[11px] sm:text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded">
                        GST exempt (vegetable / flower / fruit / household item)
                      </span>
                    )}
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowGSTDetails(!showGSTDetails)}
                      disabled={gstExempt}
                      className={`flex items-center gap-1 px-2 py-1 text-sm font-medium border rounded transition ${
                        gstExempt
                          ? 'text-gray-400 bg-gray-50 border-gray-200 cursor-not-allowed'
                          : 'text-purple-700 bg-purple-50 hover:bg-purple-100 border-purple-200'
                      }`}
                    >
                      GST
                      {showGSTDetails ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                    <button type="button" tabIndex={-1} onClick={() => setShowAdditionalCharges(!showAdditionalCharges)} className="flex items-center gap-1 px-2 py-1 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded transition">
                      Other Charges
                      {showAdditionalCharges ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                  </div>
                </div>

                {showGSTDetails && !gstExempt && (
                  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 p-3 sm:p-4 border border-purple-200 rounded-xl bg-purple-50/40 mb-3">
                    <div className="flex flex-col">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">SGST %</label>
                      <input type="text" name="sgst_percentage" value={formData.sgst_percentage} onChange={handleChange} onKeyDown={handleEnterAsTab} disabled={rcmActive || gstExempt} readOnly={rcmActive || gstExempt} className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 ${rcmActive || gstExempt ? 'bg-gray-100 cursor-not-allowed text-gray-700' : 'bg-white'}`} />
                    </div>
                    <div className="flex flex-col">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">CGST %</label>
                      <input type="text" name="cgst_percentage" value={formData.cgst_percentage} onChange={handleChange} onKeyDown={handleEnterAsTab} disabled={rcmActive || gstExempt} readOnly={rcmActive || gstExempt} className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 ${rcmActive || gstExempt ? 'bg-gray-100 cursor-not-allowed text-gray-700' : 'bg-white'}`} />
                    </div>
                    <div className="flex flex-col">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">IGST %</label>
                      <input type="text" name="igst_percentage" value={formData.igst_percentage} onChange={handleChange} onKeyDown={handleEnterAsTab} disabled={rcmActive || gstExempt} readOnly={rcmActive || gstExempt} className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 ${rcmActive || gstExempt ? 'bg-gray-100 cursor-not-allowed text-gray-700' : 'bg-white'}`} />
                    </div>
                    <div className={`p-3 rounded-xl ${Number(calculateGSTAmounts().sgst) > 0 ? 'bg-purple-50' : 'bg-gray-50 opacity-60'}`}><p className="text-xs text-gray-600 font-semibold">SGST</p><p className="text-lg font-bold text-purple-600">₹{calculateGSTAmounts().sgst}</p></div>
                    <div className={`p-3 rounded-xl ${Number(calculateGSTAmounts().cgst) > 0 ? 'bg-purple-50' : 'bg-gray-50 opacity-60'}`}><p className="text-xs text-gray-600 font-semibold">CGST</p><p className="text-lg font-bold text-purple-600">₹{calculateGSTAmounts().cgst}</p></div>
                    <div className={`p-3 rounded-xl ${Number(calculateGSTAmounts().igst) > 0 ? 'bg-purple-50' : 'bg-gray-50 opacity-60'}`}><p className="text-xs text-gray-600 font-semibold">IGST</p><p className="text-lg font-bold text-purple-600">₹{calculateGSTAmounts().igst}</p></div>
                  </div>
                )}

                {showAdditionalCharges && (
                  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 p-3 sm:p-4 border border-blue-200 rounded-xl bg-blue-50/40 mb-3">
                    {(chargeDefaults.insurance?.enabled || toNum(formData.insurance_charges) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Insurance Charge
                          {chargeDefaults.insurance?.mode === 'percent' && (
                            <span className="text-gray-400 font-normal">
                              {` (${chargeDefaults.insurance.amount || 0}%)`}
                            </span>
                          )}
                        </label>
                        <input type="text" name="insurance_charges" value={formData.insurance_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white" />
                      </div>
                    )}
                    {loadingAtBooking && (chargeDefaults.loading.enabled || toNum(formData.loading_charges) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          {normalizeChargeMode(chargeDefaults.loading?.mode) === 'percent' ? 'Loading' : 'Loading (per parcel)'}
                          {normalizeChargeMode(chargeDefaults.loading?.mode) === 'percent' && (
                            <span className="text-gray-400 font-normal">
                              {` (${chargeDefaults.loading.amount || 0}%)`}
                            </span>
                          )}
                          {normalizeChargeMode(chargeDefaults.loading?.mode) !== 'percent' && parcelCountForCharges > 0 && (
                            <span className="block text-[10px] sm:text-xs text-gray-400 font-normal mt-0.5">
                              {`₹${Number(formData.loading_charges || 0).toFixed(2)} × ${parcelCountForCharges} = ₹${Number(perParcelChargeTotal(formData.loading_charges, parcelCountForCharges)).toFixed(2)}`}
                            </span>
                          )}
                        </label>
                        <input type="text" name="loading_charges" value={formData.loading_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} disabled={normalizeChargeMode(chargeDefaults.loading?.mode) === 'percent'} className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 ${normalizeChargeMode(chargeDefaults.loading?.mode) === 'percent' ? 'bg-gray-100 cursor-not-allowed text-gray-600' : 'bg-white'}`} />
                      </div>
                    )}
                    {unloadingAtBooking && (chargeDefaults.unloading?.enabled || toNum(formData.unloading_charges) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Unloading
                          {normalizeChargeMode(chargeDefaults.unloading?.mode) === 'percent' && (
                            <span className="text-gray-400 font-normal">
                              {` (${chargeDefaults.unloading.amount || 0}%)`}
                            </span>
                          )}
                        </label>
                        <input type="text" name="unloading_charges" value={formData.unloading_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} disabled={normalizeChargeMode(chargeDefaults.unloading?.mode) === 'percent'} className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 ${normalizeChargeMode(chargeDefaults.unloading?.mode) === 'percent' ? 'bg-gray-100 cursor-not-allowed text-gray-600' : 'bg-white'}`} />
                      </div>
                    )}
                    {(chargeDefaults.toll.enabled || toNum(formData.toll_charges) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Toll</label>
                        <input type="text" name="toll_charges" value={formData.toll_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white" />
                      </div>
                    )}
                    {(chargeDefaults.fuel_surcharge.enabled || toNum(formData.fuel_surcharge) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Fuel Surcharge</label>
                        <input type="text" name="fuel_surcharge" value={formData.fuel_surcharge} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white" />
                      </div>
                    )}
                    {(chargeDefaults.packing.enabled || toNum(formData.packing_charges) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Packing</label>
                        <input type="text" name="packing_charges" value={formData.packing_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white" />
                      </div>
                    )}
                    {(chargeDefaults.handling?.enabled || toNum(formData.handling_charges) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Handling Charges</label>
                        <input type="text" name="handling_charges" value={formData.handling_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white" />
                      </div>
                    )}
                    {(chargeDefaults.docket?.enabled || toNum(formData.docket_fee) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Docket Fee</label>
                        <input type="text" name="docket_fee" value={formData.docket_fee} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white" />
                      </div>
                    )}
                    <div className="flex flex-col">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Late Charges</label>
                      <input type="text" name="late_charges" value={formData.late_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white" />
                    </div>
                    {(chargeDefaults.door_pickup?.enabled || toNum(formData.door_pickup_charges) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Door Pickup
                        </label>
                        <input type="text" name="door_pickup_charges" value={formData.door_pickup_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white" />
                        <DoorVehicleChoice
                          name="door_pickup_vehicle"
                          value={formData.door_pickup_vehicle}
                          onChange={handleChange}
                        />
                      </div>
                    )}
                    {(chargeDefaults.door_delivery?.enabled || toNum(formData.delivery_charges) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Door Delivery
                        </label>
                        <input type="text" name="delivery_charges" value={formData.delivery_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white" />
                        <DoorVehicleChoice
                          name="door_delivery_vehicle"
                          value={formData.door_delivery_vehicle}
                          onChange={handleChange}
                        />
                      </div>
                    )}
                    <div className="flex flex-col">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Other Charges</label>
                      <input type="text" name="other_charges" value={formData.other_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white" />
                    </div>
                  </div>
                )}

                <div className="space-y-0">
                  {/* Primary charges — Freight, Rate/KG, Discount */}
                  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-3 mb-3">
                    <div className="flex flex-col">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Freight Charges
                        {chargeSplitMode && (
                          <span className="text-gray-400 font-normal">
                            {` (${chargeSplit.remainderPercent || 0}%)`}
                          </span>
                        )}
                      </label>
                      <input type="text" value={calculateFreightCharges()} onKeyDown={handleEnterAsTab} disabled className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl bg-gray-100 text-gray-600 font-semibold cursor-not-allowed" />
                    </div>
                    {(isCargoMode || busFreightByWeight || toNum(formData.rate_per_kg) > 0) && (
                      <div className="flex flex-col">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Rate / KG</label>
                        <input type="text" name="rate_per_kg" value={formData.rate_per_kg} onChange={handleChange} onKeyDown={handleEnterAsTab} placeholder="From receiver hub (optional)" className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200" />
                      </div>
                    )}
                    <div className="flex flex-col">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Discount</label>
                      <input
                        type="text"
                        name="discount"
                        value={formData.discount}
                        onChange={handleChange}
                        onKeyDown={handleEnterAsTab}
                        disabled={!canSetDiscount}
                        readOnly={!canSetDiscount}
                        className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl ${
                          canSetDiscount
                            ? "bg-white focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200"
                            : "bg-gray-100 text-gray-600 cursor-not-allowed"
                        }`}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4 mt-4">
                  <div className="flex flex-col">
                    <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Total Amount</label>
                    <div className="px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-xl bg-gray-100 font-semibold text-green-600">₹{calculateTotalAmount()}</div>
                    {rcmActive && deriveRcmGst().includeInTotal === false && (
                      <p className="text-[11px] text-gray-500 mt-1">
                        GST shown above is under RCM and is not added to this total.
                      </p>
                    )}
                  </div>
                  {useShipmentClient && (
                    <p className="self-end text-[11px] text-gray-500 pb-1">
                      Credit Clients are billed monthly — payment status is set to Credit.
                    </p>
                  )}
                </div>

                {(formData.mode_of_payment === "paid" ||
                  formData.mode_of_payment === "paid_balance" ||
                  (formData.mode_of_payment !== "to_pay" && formData.mode_of_payment !== "credit")) && (
                  <div ref={paidPaymentFieldsRef} className="space-y-4 mt-4 pb-2 overflow-visible">
                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4">
                      {formData.mode_of_payment !== "to_pay" && formData.mode_of_payment !== "credit" && (
                        <div className="flex flex-col">
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Payment Mode</label>
                          <div className={`grid gap-1.5 ${formData.payment_mode && !SHIPMENT_PAYMENT_MODES.some((mode) => mode.value === formData.payment_mode) ? "grid-cols-3" : "grid-cols-2"} ${!formData.payment_mode ? "ring-2 ring-red-200 rounded-xl p-0.5" : ""}`}>
                            {formData.payment_mode
                              && !SHIPMENT_PAYMENT_MODES.some((mode) => mode.value === formData.payment_mode) && (
                                <button
                                  type="button"
                                  onClick={() => handleChange({ target: { name: "payment_mode", value: formData.payment_mode } })}
                                  className="min-w-0 px-2 py-2 sm:py-2.5 rounded-xl border text-xs sm:text-sm font-semibold transition bg-blue-50 border-blue-500 text-blue-800 shadow-sm capitalize"
                                >
                                  {formData.payment_mode.replace(/_/g, " ")}
                                </button>
                              )}
                            {SHIPMENT_PAYMENT_MODES.map((mode) => {
                              const selected = formData.payment_mode === mode.value
                              return (
                                <button
                                  key={mode.value}
                                  type="button"
                                  onClick={() => handleChange({ target: { name: "payment_mode", value: mode.value } })}
                                  className={`min-w-0 px-2 py-2 sm:py-2.5 rounded-xl border text-xs sm:text-sm font-semibold transition ${
                                    selected
                                      ? "bg-blue-50 border-blue-500 text-blue-800 shadow-sm"
                                      : "bg-white border-gray-300 text-gray-700 hover:border-blue-300 hover:bg-blue-50/40"
                                  }`}
                                >
                                  {mode.label}
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      )}
                      {(formData.mode_of_payment === "paid" || formData.mode_of_payment === "paid_balance") && (
                        <div className="flex flex-col">
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Paid Amount</label>
                          <input type="text" name="paid_amount" placeholder="0.00" value={formData.paid_amount} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200" />
                        </div>
                      )}
                    </div>
                    {(formData.mode_of_payment === "paid" || formData.mode_of_payment === "paid_balance") && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-xl">
                          <p className="text-xs text-gray-600 font-semibold">Paid Amount</p>
                          <p className="text-lg font-bold text-blue-600">₹{formData.paid_amount || "0.00"}</p>
                        </div>
                        <div className="bg-blue-50 p-3 rounded-xl">
                          <p className="text-xs text-gray-600 font-semibold">Balance Due</p>
                          <p className="text-lg font-bold text-blue-600">₹{calculateBalanceAmount()}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
            </div>
          </div>
          )}

        </div>
        </div>
      </form>

      {isMobileLayout && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 px-4 py-3 flex items-center justify-between gap-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] md:hidden">
          {mobileStep > 0 ? (
            <Button variant="secondary" type="button" icon={ChevronLeft} onClick={goToPrevMobileStep}>Back</Button>
          ) : (
            <div className="w-16" />
          )}
          <Button
            variant="primary"
            type="button"
            icon={mobileStep < agentMobileLastStep ? ChevronRight : undefined}
            iconPosition="right"
            onClick={handleMobilePrimaryAction}
            loading={saving && mobileStep >= agentMobileLastStep}
            disabled={saving}
          >
            {mobileStep < agentMobileLastStep
              ? "Next"
              : saving
                ? "Sending..."
                : (isAgentRole ? "Send request" : "Update Shipment")}
          </Button>
        </div>
      )}

      {showEwayBillModal && !ewaybillFeatureOn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-lg w-[95%] max-w-md">
            <div className="flex items-center justify-between px-4 py-2 bg-blue-600 text-white rounded-t-sm">
              <h3 className="text-sm font-semibold">Add E-WayBill</h3>
              <Button variant="ghost" onClick={handleCloseEwayBillModal} className="text-white hover:bg-white/20">✕</Button>
            </div>
            <div className="p-4 space-y-3">
              {ewayBills.map((value, index) => {
                const isValid = isValidEway(value)
                return (
                  <div key={index}>
                    <input
                      type="text"
                      value={value}
                      maxLength={12}
                      inputMode="numeric"
                      placeholder="Enter 12-digit E-Way Bill No."
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "")
                        const updated = [...ewayBills]
                        updated[index] = val
                        setEwayBills(updated)
                      }}
                      className={`w-full px-3 py-2 border rounded-xl ${value && !isValid ? "border-red-500" : "border-gray-300"} focus:outline-none`}
                    />
                    {value && !isValid && <p className="text-xs text-red-500 mt-1">E-Way Bill must be exactly 12 digits</p>}
                  </div>
                )
              })}
              <div className="flex gap-2 pt-2">
                <Button variant="secondary" size="sm" disabled={!isValidEway(ewayBills[ewayBills.length - 1])} onClick={() => setEwayBills([...ewayBills, ""])}>Add New</Button>
                <Button variant="danger" size="sm" disabled={ewayBills.length === 1} onClick={() => setEwayBills(ewayBills.slice(0, -1))}>Remove Last</Button>
                <Button variant="success" size="sm" disabled={!ewayBills.some(isValidEway)} onClick={handleSubmitEwayBills}>Submit</Button>
              </div>
            </div>
            <div className="flex justify-end px-4 py-2 border-t">
              <Button variant="secondary" size="sm" onClick={handleCloseEwayBillModal}>Close</Button>
            </div>
          </div>
        </div>
      )}

      {showUpdateVehicleModal && ewaybillFeatureOn && selectedEwayBill && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-lg w-[95%] max-w-md">
            <div className="flex items-center justify-between px-4 py-2 bg-blue-600 text-white rounded-t-sm">
              <h3 className="text-sm font-semibold">Update Vehicle / Part-B</h3>
              <Button variant="ghost" onClick={() => { setShowUpdateVehicleModal(false); setSelectedEwayBill(null) }} className="text-white hover:bg-white/20">✕</Button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-xs text-gray-500">EWB: {selectedEwayBill.eway_bill_number}</p>
              <div>
                <label className="text-xs font-medium text-gray-600">Vehicle No *</label>
                <input
                  type="text"
                  value={updateVehicleForm.vehicleNo}
                  onChange={(e) => setUpdateVehicleForm((prev) => ({ ...prev, vehicleNo: e.target.value.toUpperCase() }))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-xl"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Reason</label>
                <input
                  type="text"
                  value={updateVehicleForm.reasonRem}
                  onChange={(e) => setUpdateVehicleForm((prev) => ({ ...prev, reasonRem: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-xl"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" size="sm" onClick={() => { setShowUpdateVehicleModal(false); setSelectedEwayBill(null) }}>Close</Button>
                <Button variant="primary" size="sm" loading={ewayActionLoading} disabled={ewayActionLoading} onClick={handleUpdateEwayVehicle}>
                  Update
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AddPackageDimensionModal
        isOpen={showAddPackageModal}
        onClose={() => {
          setShowAddPackageModal(false)
          setAddPackageAppendMode(false)
        }}
        existingParcels={parcels}
        appendMode={addPackageAppendMode}
        onSave={setParcels}
        ratePerKg={formData.rate_per_kg}
      />
      <EditPackageDimensionModal
        isOpen={showEditPackageModal}
        onClose={() => setShowEditPackageModal(false)}
        parcels={parcels}
        onSave={setParcels}
        ratePerKg={formData.rate_per_kg}
      />
      <ViewPackageDimensionModal
        isOpen={showViewPackageModal}
        onClose={() => setShowViewPackageModal(false)}
        parcels={parcels}
        ratePerKg={formData.rate_per_kg}
      />
    </DashboardLayout>
  )
}

export default EditShipment;
